import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useParams } from 'react-router-dom';
// TODO: Import via props or create satelliteApi wrapper
// import MissionService from '../../../../service/MissionService';
import { createSatelliteGroup, editSatelliteGroup, getSatelliteGroups, getAllSatelliteGroupOrbit, uploadTle } from '../../../../service/satelliteApi';
import { useMDCockpit } from '../../../../hooks/useMDCockpit';
import { useCesium } from '../../../../context/CesiumContext';
import type { Satellite, SatGroup, OrbitMeta, OrbitType } from '../../../../types/mission';
import type { KeplerianOrbitParams } from '../../../../lib/orbitPropagator';
import { applyOrbitReportUpdatesToTreeItems, mapApiSatToTreeItem } from '../../../../lib/missionSimulationMapper';
import type { ModelData } from '../../../../types/mdai';
import { getUtcNowDisplay, validateUtcDisplay, displayToEpoch, epochToDisplay } from '../../../../lib/datetime';
import {
  parseTleText,
  parseTleFileContent,
  resolveTleForPreview,
  normalizeTlePair,
  validateTlePair,
  type TleEntry,
} from '../../../../lib/tle';
import { NAME_MAX_LENGTH, enforceNameConstraints, checkSpecialCharacters } from '../../../../lib/validation';
import { showToastMessage, preventKeyInputs } from '../../../../../../../utils/commonFunctions';
import CustomToolTip from '../../Common/CustomToolTip';
import UtcDateTimeField from '../../../SettingLayer/components/UtcDateTimeField';
import TleInput from './TleInput';
import styles from './AddSatModal.module.scss';

// ── Types ─────────────────────────────────────────────────────────────────────

type TabId = 'satellite' | 'group' | 'tle';
type InputType = 'keplerian' | 'cartesian' | 'equinoctial';
type GroupType = 'single' | 'multi';
type ValidationErrors = Record<string, string>;

// ── Pure helpers ──────────────────────────────────────────────────────────────

function pad(n: number) { return String(n).padStart(2, '0'); }

const IST_OFFSET_MINS = 330;

function convertEpoch(raw: string, toTz: 'UTC' | 'IST'): string {
  if (validateUtcDisplay(raw) || !raw) return raw;
  const delta = toTz === 'IST' ? IST_OFFSET_MINS : -IST_OFFSET_MINS;
  const [datePart, timePart] = raw.split(' ');
  let [y, mo, d] = datePart.split('-').map(Number);
  let [h, mi, s] = timePart.split(':').map(Number);
  mi += delta;
  h += Math.floor(mi / 60); mi = ((mi % 60) + 60) % 60;
  d += Math.floor(h / 24); h = ((h % 24) + 24) % 24;
  return `${y}-${pad(mo)}-${pad(d)} ${pad(h)}:${pad(mi)}:${pad(s)}`;
}

function toggleAlt(val: string, from: 'km' | 'm'): { value: string; unit: 'km' | 'm' } {
  const n = parseFloat(val) || 0;
  return from === 'km'
    ? { value: String(+(n * 1000).toFixed(3)), unit: 'm' }
    : { value: String(+(n / 1000).toFixed(6)), unit: 'km' };
}

function toggleAngle(val: string, from: 'deg' | 'rad'): { value: string; unit: 'deg' | 'rad' } {
  const n = parseFloat(val) || 0;
  return from === 'deg'
    ? { value: String(+(n * Math.PI / 180).toFixed(8)), unit: 'rad' }
    : { value: String(+(n * 180 / Math.PI).toFixed(6)), unit: 'deg' };
}

function parseFiniteNumber(value: string): number | null {
  if (!value.trim()) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function isIntegerInRange(value: string, min: number, max: number): boolean {
  const n = parseFiniteNumber(value);
  return n !== null && Number.isInteger(n) && n >= min && n <= max;
}

function isValidLtan(value: string): boolean {
  const m = value.match(/^(\d{1,2}):(\d{2}):(\d{2})$/);
  if (!m) return false;
  const [, hh, mm, ss] = m;
  return Number(hh) >= 0 && Number(hh) <= 23 && Number(mm) <= 59 && Number(ss) <= 59;
}

function inRange(value: number, min: number, max: number): boolean {
  return value >= min && value <= max;
}

function limitDecimalPlaces(value: string, maxDecimals: number = 8): string {
  if (!value.includes('.')) return value;
  const [integerPart, decimalPart] = value.split('.');
  if (decimalPart.length > maxDecimals) {
    return `${integerPart}.${decimalPart.slice(0, maxDecimals)}`;
  }
  return value;
}

interface GroupOrbitConfig {
  groupType: GroupType;
  numSats: string;
  numPlanes: string;
  planeSepAngle: string;
  interPlanePhasing: string;
  groupAngleUnit: 'deg' | 'rad';
  evenSpacing: boolean;
  evenInterPhasing: boolean;
  orbitType?: string;
}

function buildGroupOrbits(base: KeplerianOrbitParams, cfg: GroupOrbitConfig): KeplerianOrbitParams[] {
  const nSatsInput = Math.max(1, parseInt(cfg.numSats, 10) || 1);
  const isMulti = cfg.groupType === 'multi';
  const nPlanes = isMulti ? Math.max(1, parseInt(cfg.numPlanes, 10) || 1) : 1;
  const satsInPlane = nSatsInput;
  const toDeg = (val: string) =>
    cfg.groupAngleUnit === 'rad' ? (parseFloat(val) || 0) * (180 / Math.PI) : parseFloat(val) || 0;

  const baseAngle = cfg.orbitType === 'sso' ? 180 : 360;

  const planeSepDeg = isMulti
    ? (cfg.evenSpacing ? baseAngle / nPlanes : toDeg(cfg.planeSepAngle))
    : 0;
  const interPhasingDeg = cfg.evenInterPhasing
    ? (satsInPlane > 0 ? baseAngle / satsInPlane : 0)
    : toDeg(cfg.interPlanePhasing);
  const orbits: KeplerianOrbitParams[] = [];
  for (let p = 0; p < nPlanes; p++) {
    for (let s = 0; s < satsInPlane; s++) {
      orbits.push({
        ...base,
        RAAN: (base.RAAN + p * planeSepDeg) % 360,
        TA: (base.TA + s * interPhasingDeg) % 360,
      });
    }
  }
  return orbits;
}

// ── SVG Icons ─────────────────────────────────────────────────────────────────

const SatIcon = () => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
    <path d="M19.3087 10.6912L16.0981 7.48059C15.984 7.36655 15.7983 7.36602 15.6913 7.48669C14.8058 8.48525 14.4079 9.76502 14.4971 11.0182M14.4971 11.0182C14.5768 12.1387 15.0459 13.238 15.9039 14.096C16.7156 14.9078 17.9244 15.3877 18.9817 15.5029C20.2915 15.6455 21.4884 15.2391 22.5143 14.3098C22.6339 14.2015 22.6334 14.0159 22.5193 13.9019L19.3087 10.6912M19.3087 10.6912L21.0598 8.94009M14.4971 11.0182L12.5537 12.9616M18.9817 15.5029L17.0383 17.4462M12.5537 12.9616L9.81242 10.2203C9.69838 10.1063 9.51348 10.1063 9.39944 10.2203L6.11113 13.5086C5.99708 13.6227 5.99708 13.8076 6.11113 13.9216L8.85241 16.6629M12.5537 12.9616L8.85241 16.6629M8.85241 16.6629L8.07433 17.441C7.31405 18.2013 7.31405 19.4339 8.07433 20.1942L9.80574 21.9256C10.566 22.6859 11.7987 22.6859 12.559 21.9256L13.337 21.1475M17.0383 17.4462L13.337 21.1475M17.0383 17.4462L19.7796 20.1875C19.8937 20.3016 19.8937 20.4865 19.7796 20.6005L16.4913 23.8888C16.3773 24.0029 16.1924 24.0029 16.0783 23.8888L13.337 21.1475" stroke="currentColor" strokeWidth="1.125" strokeLinecap="round" />
  </svg>
);

const GroupIcon = () => (
  <svg width="23" height="23" viewBox="0 0 23 23" fill="none">
    <path d="M10.1911 3.87017C10.4327 4.24336 10.8527 4.49031 11.3304 4.49031C11.8115 4.49031 12.2341 4.23979 12.4749 3.86208M10.1911 3.87017C10.0538 3.65816 9.97413 3.40541 9.97413 3.13404C9.97413 2.38499 10.5814 1.77777 11.3304 1.77777C12.0794 1.77777 12.6867 2.38499 12.6867 3.13404C12.6867 3.40196 12.609 3.65175 12.4749 3.86208M10.1911 3.87017C9.18484 5.22942 8.48193 8.32562 8.48193 11.9277C8.48193 16.7842 9.75974 20.7213 11.336 20.7213C12.2042 20.7213 12.9818 19.5269 13.5053 17.6425M12.4749 3.86208C12.88 4.40592 13.2363 5.23105 13.5176 6.25741M14.0457 9.15899C14.1329 9.96899 14.1828 10.8312 14.1893 11.7265" stroke="currentColor" strokeWidth="0.9375" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9.91259 9.45595C14.1185 7.02766 18.167 6.16576 18.9551 7.53084C19.7213 8.858 17.1238 11.8015 13.1138 14.1955M5.94006 12.2985C4.89369 13.2464 4.14618 14.164 3.80235 14.935M8.7811 16.2625C7.2072 16.817 5.83877 17.0603 4.90205 16.9441M4.90205 16.9441C5.00957 16.7498 5.07078 16.5262 5.07078 16.2884C5.07078 15.5689 4.51046 14.9802 3.80235 14.935C3.77331 14.9331 3.74402 14.9322 3.71451 14.9322C2.96546 14.9322 2.35824 15.5394 2.35824 16.2884C2.35824 17.0375 2.96546 17.6447 3.71451 17.6447C4.22574 17.6447 4.67092 17.3618 4.90205 16.9441Z" stroke="currentColor" strokeWidth="0.9375" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8.87075 7.6242C6.29898 6.70324 4.25905 6.60288 3.72366 7.5302C2.93554 8.89528 5.7062 11.9704 9.91212 14.3987C13.0339 16.2011 16.069 17.1405 17.7485 16.9468M18.8916 14.9689C18.1713 15.0013 17.5973 15.5955 17.5973 16.3238C17.5973 16.5484 17.6519 16.7603 17.7485 16.9468M18.8916 14.9689C18.9122 14.968 18.9328 14.9675 18.9535 14.9675C19.7026 14.9675 20.3098 15.5748 20.3098 16.3238C20.3098 17.0729 19.7026 17.6801 18.9535 17.6801C18.4291 17.6801 17.9742 17.3824 17.7485 16.9468M18.8916 14.9689C18.6208 14.3431 18.0799 13.6184 17.3258 12.858" stroke="currentColor" strokeWidth="0.9375" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TleIcon = () => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
    <path d="M13.6273 15.1315C14.4586 15.1315 15.1325 14.4576 15.1325 13.6263C15.1325 12.795 14.4586 12.1211 13.6273 12.1211C12.796 12.1211 12.1221 12.795 12.1221 13.6263C12.1221 14.4576 12.796 15.1315 13.6273 15.1315Z" stroke="currentColor" strokeWidth="0.9375" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M18.9752 13.627C18.9752 16.5808 16.5807 18.9753 13.6269 18.9753M18.9752 13.627C18.9752 10.6732 16.5807 8.27872 13.6269 8.27872M18.9752 13.627H21.6493M13.6269 18.9753C10.6731 18.9753 8.27863 16.5808 8.27863 13.627M13.6269 18.9753V21.6505M8.27863 13.627C8.27863 10.6732 10.6731 8.27872 13.6269 8.27872M8.27863 13.627H5.60449M13.6269 8.27872V5.60352" stroke="currentColor" strokeWidth="0.9375" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M24.6047 6.94141L24.2683 7.06995C22.506 7.74352 20.8264 8.57685 19.2465 9.55261M6.94043 24.6053C7.61603 22.7726 8.46392 21.0264 9.46509 19.386" stroke="currentColor" strokeWidth="0.9375" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CloseIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M2 2l8 8M10 2L2 10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

const MinimizeIcon = ({ minimized = false }: { minimized?: boolean }) => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d={minimized ? 'M6 2v8M2 6h8' : 'M2 6h8'} stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

const ChevronDown = ({ size = 10 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const EvenIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
    <circle cx="6" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="18" cy="12" r="2" />
    <path d="M8 12h2M14 12h2" />
  </svg>
);

const PhasingIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
    <circle cx="5" cy="8" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="19" cy="8" r="1.6" />
    <circle cx="5" cy="16" r="1.6" /><circle cx="19" cy="16" r="1.6" />
    <path d="M6.3 8.6 10.7 11M13.3 11 17.7 8.6M6.3 15.4 10.7 13M13.3 13 17.7 15.4" />
  </svg>
);

// ── Sub-components ────────────────────────────────────────────────────────────



interface TooltipTextProps {
  title: string;
  children: React.ReactNode;
  className?: string;
}

const TooltipText: React.FC<TooltipTextProps> = ({ title, children, className }) => (
  <CustomToolTip title={title} placement="top-start" className={styles.tooltipWrap}>
    <span className={className}>{children}</span>
  </CustomToolTip>
);

const FieldLabel: React.FC<TooltipTextProps> = ({ title, children }) => (
  <TooltipText title={title} className={styles.fieldLabel}>
    {children}
  </TooltipText>
);

interface SelectInputProps {
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
  dropdownAlign?: 'left' | 'right';
  dropdownWidth?: number;
  className?: string;
  style?: React.CSSProperties;
}
const SelectInput: React.FC<SelectInputProps> = ({ value, options, onChange, dropdownAlign = 'left', dropdownWidth, className, style }) => {
  const [open, setOpen] = useState(false);
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);
  const [fontSize, setFontSize] = useState<string | undefined>(undefined);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);

  const handleOpen = () => {
    if (!open && triggerRef.current) {
      setTriggerRect(triggerRef.current.getBoundingClientRect());
      const computedFontSize = window.getComputedStyle(triggerRef.current).fontSize;
      setFontSize(computedFontSize);
    }
    setOpen(o => !o);
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!triggerRef.current?.contains(t) && !dropRef.current?.contains(t)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler, true);
    return () => document.removeEventListener('mousedown', handler, true);
  }, [open]);

  const current = options.find(o => o.value === value)?.label ?? value;
  const menuWidth = dropdownWidth ?? triggerRect?.width ?? 0;
  const menuLeft = triggerRect
    ? (dropdownAlign === 'right' ? triggerRect.right - menuWidth : triggerRect.left)
    : 0;

  const dropdown = open && triggerRect
    ? ReactDOM.createPortal(
      <div
        ref={dropRef}
        className={styles.dropdownPop}
        style={{ position: 'fixed', top: triggerRect.bottom + 8, left: menuLeft, width: menuWidth, zIndex: 9999, fontSize }}
        onClick={e => e.stopPropagation()}
      >
        {options.map(o => (
          <div
            key={o.value}
            className={`${styles.dropdownOpt} ${value === o.value ? styles.dropdownOptActive : ''}`}
            onClick={(e) => { e.stopPropagation(); onChange(o.value); setOpen(false); }}
          >
            {o.label}
          </div>
        ))}
      </div>,
      document.body,
    )
    : null;

  return (
    <div ref={triggerRef} className={`${styles.selectInput} ${className || ''}`} style={style} onClick={handleOpen}>
      <span>{current}</span>
      <ChevronDown />
      {dropdown}
    </div>
  );
};

interface NumericInputProps {
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  error?: boolean;
  disabled?: boolean;
  suffix?: string;
  onSuffixClick?: () => void;
  suffixTitle?: string;
  width?: number;
  placeholder?: string;
  leftChip?: string;
  onLeftChipClick?: () => void;
  leftChipTitle?: string;
  allowNegative?: boolean;
}
const NumericInput: React.FC<NumericInputProps> = ({
  value,
  onChange,
  onBlur,
  error,
  disabled,
  suffix,
  onSuffixClick,
  suffixTitle,
  width,
  placeholder,
  leftChip,
  onLeftChipClick,
  leftChipTitle,
  allowNegative = false,
}) => {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    const keysToPrevent = allowNegative ? ['e', 'E', '+'] : ['e', 'E', '+', '-'];
    if (!preventKeyInputs(e, keysToPrevent)) return;

    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      const numValue = parseFloat(value);

      let step = 1;
      if (e.shiftKey) {
        step = 10;
      } else if (e.altKey || e.ctrlKey) {
        step = 0.1;
      }

      if (isNaN(numValue)) {
        onChange(e.key === 'ArrowUp' ? step.toString() : (!allowNegative ? step.toString() : (-step).toString()));
      } else {
        const decimals = value.includes('.') ? value.split('.')[1].length : 0;
        const targetDecimals = Math.min(Math.max(decimals, step === 0.1 ? 1 : 0), 8);
        const newValue = e.key === 'ArrowUp' ? numValue + step : numValue - step;
        if (!allowNegative && newValue < 0) {
          onChange('0');
        } else {
          onChange(parseFloat(newValue.toFixed(targetDecimals)).toString());
        }
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;
    const regex = allowNegative ? /^-?\d*\.?\d*$/ : /^\d*\.?\d*$/;
    if (val === '' || regex.test(val)) {
      if (val.includes('.')) {
        const [integerPart, decimalPart] = val.split('.');
        if (decimalPart.length > 8) {
          val = `${integerPart}.${decimalPart.slice(0, 8)}`;
        }
      }
      onChange(val);
    } else if (!allowNegative && val.includes('-')) {
      showToastMessage('info', 'Negative values are not allowed for this field');
    }
  };

  const hasSegments = !!leftChip || !!suffix;
  if (hasSegments) {
    return (
      <div
        className={`${styles.segmentedInputGroup} ${error ? styles.fieldInputError : ''} ${disabled ? styles.fieldDisabled : ''}`}
        style={width ? { width } : undefined}
      >
        {leftChip && (
          onLeftChipClick ? (
            <CustomToolTip title={leftChipTitle || ''} placement="top" className={styles.tooltipWrap} disabled={!leftChipTitle}>
              <div className={`${styles.segmentLeft} ${styles.segmentClickable}`} onClick={onLeftChipClick}>{leftChip}</div>
            </CustomToolTip>
          ) : (
            <div className={styles.segmentLeft}>{leftChip}</div>
          )
        )}
        <input
          className={styles.segmentMiddle}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={onBlur}
          disabled={disabled}
          placeholder={placeholder}
        />
        {suffix && (
          onSuffixClick ? (
            <CustomToolTip title={suffixTitle || ''} placement="top" className={styles.tooltipWrap} disabled={!suffixTitle}>
              <div className={`${styles.segmentRight} ${styles.segmentClickable}`} onClick={onSuffixClick}>{suffix}</div>
            </CustomToolTip>
          ) : (
            <div className={styles.segmentRight}>{suffix}</div>
          )
        )}
      </div>
    );
  }

  return (
    <div
      className={`${styles.fieldInput} ${error ? styles.fieldInputError : ''} ${disabled ? styles.fieldDisabled : ''}`}
      style={width ? { width } : undefined}
    >
      <input
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={onBlur}
        disabled={disabled}
        placeholder={placeholder}
      />
    </div>
  );
};

interface AccordionSectionProps {
  title: string;
  children: React.ReactNode;
  isOpen?: boolean;
  onToggle?: (open: boolean) => void;
  defaultOpen?: boolean;
}
const AccordionSection: React.FC<AccordionSectionProps> = ({ title, children, isOpen, onToggle, defaultOpen = true }) => {
  const [localOpen, setLocalOpen] = useState(defaultOpen);
  const open = isOpen !== undefined ? isOpen : localOpen;

  const handleToggle = () => {
    if (isOpen !== undefined) {
      onToggle?.(!isOpen);
    } else {
      setLocalOpen(o => !o);
    }
  };

  return (
    <div className={styles.fsSection}>
      <div className={styles.fsSectionHd} onClick={handleToggle}>
        <div className={styles.fsSectionHdLeft}>
          <span className={`${styles.accordionCaret} ${open ? '' : styles.accordionCaretOpen}`}>
            <ChevronDown size={10} />
          </span>
          <TooltipText title={title}>{title}</TooltipText>
        </div>
      </div>
      {open && <div className={styles.fsSectionBody}>{children}</div>}
    </div>
  );
};

// ── Option lists ──────────────────────────────────────────────────────────────

const ORBIT_OPTS = [
  { value: 'inclined', label: 'Inclined' },
  { value: 'sso', label: 'SSO' },
  { value: 'geo', label: 'GEO' },
  { value: 'heo', label: 'HEO' },
];

const GROUP_ORBIT_OPTS = ORBIT_OPTS.filter(o => o.value !== 'geo' && o.value !== 'heo');

const INPUT_OPTS = [
  { value: 'keplerian', label: 'Keplerian' },
  { value: 'cartesian', label: 'Cartesian' },
  { value: 'equinoctial', label: 'Equinoctial' },
];

const REF_FRAME_KEPLERIAN = [
  { value: 'EME2000', label: 'EME2000' },
  { value: 'GCRF', label: 'GCRF' },
];

const REF_FRAME_CARTESIAN = [
  { value: 'Inertial', label: 'Inertial' },
  { value: 'Fixed', label: 'Fixed' },
];

const GROUP_TYPE_OPTS = [
  { value: 'single', label: 'Single Plane' },
  { value: 'multi', label: 'Multi Plane' },
];

/*
const DEFAULT_PHYSICAL_CHARACTERISTICS = {
  mass_kg: 12.4,
  volume_m3: 0.04,
  dragCoefficient: 2.2,
  dragSurfaceArea_m2: 0.04,
  reflectCoefficient: 1.3,
  srpSurfaceArea_m2: 0.04,
};
*/

const DEFAULT_INTERNAL_PHYSICAL_CHARACTERISTICS = {
  mass: 12.4,
  volume: 0.04,
  cd: 2.2,
  dragArea: 0.04,
  cr: 1.3,
  srpArea: 0.04,
  inertia: { Ixx: 0, Iyy: 0, Izz: 0, Ixy: 0, Ixz: 0, Iyz: 0 },
  orientation: { Roll: 0, Pitch: 0, Yaw: 0 },
};

// ── Orbit state ───────────────────────────────────────────────────────────────

const defaultOrbit = (defaultEpoch?: string) => ({
  orbitType: 'inclined' as OrbitType,
  inputType: 'keplerian' as InputType,
  // Epoch
  epoch: defaultEpoch || getUtcNowDisplay(),
  epochTz: 'UTC' as 'UTC' | 'IST',
  epochError: '',
  // Units
  altUnit: 'km' as 'km' | 'm',
  altDatum: 'MSL' as 'MSL' | 'WGS84',
  angleUnit: 'deg' as 'deg' | 'rad',
  smaUnit: 'km' as 'km' | 'm',
  // Keplerian
  altitude: '500',
  smaMode: 'altitude' as 'altitude' | 'sma',
  sma: String(500 + 6371),
  eccentricity: '0',
  inclination: '37',
  raan: '0',
  aop: '0',
  trueAnomaly: '0',
  refFrame: 'EME2000',
  // SSO
  ltan: '10:30:00',
  // GEO
  longitude: '0',
  // HEO
  perigee: '600',
  apogee: '39600',
  // Cartesian
  posX: '6878', posY: '0', posZ: '0',
  velX: '0', velY: '6.04', velZ: '4.55',
  posUnit: 'km' as 'km' | 'm',
  velUnit: 'km/s' as 'km/s' | 'm/s',
  cartRefFrame: 'Inertial',
  // Equinoctial
  hx: '0', hy: '-31303.9', hz: '41588.5',
  ex: '0', ey: '0', ez: '0',
  hUnit: 'km2/s' as 'km2/s' | 'm2/s',
});

type OrbitState = ReturnType<typeof defaultOrbit>;

const orbitStateFromSatellite = (sat: Satellite): OrbitState => {
  const base = defaultOrbit();
  const meta = sat.orbitMeta;
  const orb = sat.orbitElements;
  const orbitType = meta?.orbitType ?? 'inclined';
  const inputType = meta?.inputType ?? 'keplerian';
  const epoch = meta?.epoch?.replace(' UTC', '') ?? (orb ? new Date(orb.epoch * 1000).toISOString().slice(0, 19).replace('T', ' ') : base.epoch);
  const altitude = meta?.altitude_km != null
    ? String(meta.altitude_km)
    : orb ? String(Number(((orb.eccentricity && orb.eccentricity > 0 ? orb.semiMajorAxis * (1 - orb.eccentricity) : orb.semiMajorAxis) - EARTH_RADIUS_KM).toFixed(4))) : base.altitude;
  const altNum = parseFloat(altitude) || 0;
  const sma = orb?.semiMajorAxis != null ? String(Number(orb.semiMajorAxis.toFixed(4))) : String(Number((altNum + EARTH_RADIUS_KM).toFixed(4)));

  return {
    ...base,
    orbitType,
    inputType,
    epoch,
    altitude,
    sma,
    eccentricity: meta?.eccentricity != null ? String(meta.eccentricity) : orb ? String(orb.eccentricity) : base.eccentricity,
    inclination: meta?.inclination_deg != null ? String(meta.inclination_deg) : orb ? String(orb.inclination) : base.inclination,
    raan: meta?.raan_deg != null ? String(meta.raan_deg) : orb ? String(orb.RAAN) : base.raan,
    aop: meta?.aop_deg != null ? String(meta.aop_deg) : orb ? String(orb.AOP) : base.aop,
    trueAnomaly: meta?.trueAnomaly_deg != null ? String(meta.trueAnomaly_deg) : orb ? String(orb.TA) : base.trueAnomaly,
    refFrame: meta?.refFrame ?? base.refFrame,
    ltan: meta?.ltan ?? base.ltan,
    longitude: meta?.longitude_deg != null ? String(meta.longitude_deg) : base.longitude,
    perigee: meta?.perigee_km != null ? String(meta.perigee_km) : base.perigee,
    apogee: meta?.apogee_km != null ? String(meta.apogee_km) : base.apogee,
    // Cartesian position and velocity coordinates
    posX: meta?.position?.[0] != null ? String(meta.position[0]) : base.posX,
    posY: meta?.position?.[1] != null ? String(meta.position[1]) : base.posY,
    posZ: meta?.position?.[2] != null ? String(meta.position[2]) : base.posZ,
    velX: meta?.velocity?.[0] != null ? String(meta.velocity[0]) : base.velX,
    velY: meta?.velocity?.[1] != null ? String(meta.velocity[1]) : base.velY,
    velZ: meta?.velocity?.[2] != null ? String(meta.velocity[2]) : base.velZ,
    // Equinoctial parameters
    ex: meta?.eccentricityVector?.[0] != null ? String(meta.eccentricityVector[0]) : base.ex,
    ey: meta?.eccentricityVector?.[1] != null ? String(meta.eccentricityVector[1]) : base.ey,
    hx: meta?.inclinationVector?.[0] != null ? String(meta.inclinationVector[0]) : base.hx,
    hy: meta?.inclinationVector?.[1] != null ? String(meta.inclinationVector[1]) : base.hy,
  };
};

const orbitStateFromGroup = (group: SatGroup): OrbitState => {
  const ref = group.children?.[0];
  if (!ref) return defaultOrbit();
  return orbitStateFromSatellite(ref);
};

// ── Orbital details form ──────────────────────────────────────────────────────

interface OrbitalFormProps {
  s: OrbitState;
  set: (patch: Partial<OrbitState>) => void;
  allowInputType?: boolean;
  errors?: ValidationErrors;
  clearError?: (key: string) => void;
}

const OrbitalDetailsForm: React.FC<OrbitalFormProps> = ({ s, set, allowInputType = true, errors = {}, clearError }) => {
  const isInclined = s.orbitType === 'inclined';
  const isSSO = s.orbitType === 'sso';
  const isGEO = s.orbitType === 'geo';
  const isHEO = s.orbitType === 'heo';
  const isKeplerian = s.inputType === 'keplerian';
  const isCartesian = s.inputType === 'cartesian';
  const isEquinoctial = s.inputType === 'equinoctial';

  const altLabel = s.altUnit === 'km' ? 'km' : 'm';
  const angleLabel = s.angleUnit === 'deg' ? '°' : 'rad';
  const hasError = (key: string) => !!errors[key];
  const err = (key: string) => errors[key];
  const setField = useCallback((key: keyof OrbitState, value: string) => {
    clearError?.(key);
    clearError?.('orbit');

    // On-the-fly validation for limits
    const num = parseFloat(value);
    if (!isNaN(num)) {
      if (key === 'altitude' || key === 'perigee' || key === 'apogee') {
        const altKm = altToKm(value, s.altUnit);
        const isMeter = s.altUnit === 'm';
        const minAlt = isMeter ? ORBIT_LIMITS.altitudeKm.min * 1000 : ORBIT_LIMITS.altitudeKm.min;
        const maxLimitKm = (key === 'altitude' && s.orbitType === 'sso') ? 5974 : ORBIT_LIMITS.altitudeKm.max;
        const maxAlt = isMeter ? maxLimitKm * 1000 : maxLimitKm;

        if (altKm < 0 || altKm > maxLimitKm || (value.length >= 3 && altKm < ORBIT_LIMITS.altitudeKm.min)) {
          const label = key === 'altitude' ? 'Altitude' : key === 'perigee' ? 'Perigee' : 'Apogee';
          showToastMessage('info', `${label} must be between ${minAlt} and ${maxAlt} ${s.altUnit}`);
          return;
        }
      } else if (key === 'eccentricity') {
        const R_Earth = 6378;
        const h_perigee_min = 150;
        const altKm = s.smaMode === 'sma'
          ? (s.smaUnit === 'm' ? (parseFloat(s.sma) || 0) / 1000 : (parseFloat(s.sma) || 0)) - R_Earth
          : altToKm(s.altitude || '500', s.altUnit);
        const a = R_Earth + altKm;
        const r_p_min = R_Earth + h_perigee_min;
        const e_max = (a - r_p_min) / a;

        if (num < 0) {
          showToastMessage('info', 'Eccentricity cannot be negative');
          return;
        }
        if (num >= 1) {
          showToastMessage('info', 'Eccentricity must be < 1 for a closed orbit (e≥1 is parabolic/hyperbolic)');
          return;
        }
        if (num > e_max) {
          showToastMessage('info', ` Max allowed eccentricity for ${parseFloat(altKm.toFixed(6))} km altitude is ${parseFloat(e_max.toFixed(6))}`);
          return;
        }
      } else if (key === 'inclination') {
        const minInc = s.orbitType === 'sso' ? ORBIT_LIMITS.ssoInclinationDeg.min : ORBIT_LIMITS.inclinationDeg.min;
        const maxInc = s.orbitType === 'sso' ? ORBIT_LIMITS.ssoInclinationDeg.max : ORBIT_LIMITS.inclinationDeg.max;
        const inc = angleConvert(value, s.angleUnit);
        const isRad = s.angleUnit === 'rad';
        const displayMin = isRad ? +(minInc * Math.PI / 180).toFixed(4) : minInc;
        const displayMax = isRad ? +(maxInc * Math.PI / 180).toFixed(4) : maxInc;
        const unitSymbol = isRad ? ' rad' : '°';

        if (inc < 0 || inc > maxInc || (s.orbitType === 'sso' && value.length >= 3 && inc < minInc)) {
          showToastMessage('info', `Inclination must be between ${displayMin}${unitSymbol} and ${displayMax}${unitSymbol}`);
          return;
        }
      } else if (['raan', 'aop', 'trueAnomaly'].includes(key)) {
        const angle = angleConvert(value, s.angleUnit);
        const maxAngle = ORBIT_LIMITS.orbitalAngleDeg.max;
        const isRad = s.angleUnit === 'rad';
        const displayMax = isRad ? +(maxAngle * Math.PI / 180).toFixed(4) : maxAngle;
        const unitSymbol = isRad ? ' rad' : '°';
        if (angle < 0 || angle > maxAngle) {
          const nameMap: Record<string, string> = { raan: 'RAAN', aop: 'AOP', trueAnomaly: 'TA' };
          showToastMessage('info', `${nameMap[key]} must be between 0 and ${displayMax}${unitSymbol}`);
          return;
        }
      } else if (key === 'longitude') {
        const lon = angleConvert(value, s.angleUnit);
        const maxLon = ORBIT_LIMITS.longitudeDeg.max;
        const isRad = s.angleUnit === 'rad';
        const displayMax = isRad ? +(maxLon * Math.PI / 180).toFixed(4) : maxLon;
        const unitSymbol = isRad ? ' rad' : '°';
        if (Math.abs(lon) > maxLon) {
          showToastMessage('info', `Longitude must be between -${displayMax}${unitSymbol} and ${displayMax}${unitSymbol}`);
          return;
        }
      }
    }

    const newState: Partial<OrbitState> = { [key]: value };
    if (key === 'altitude' || key === 'eccentricity') {
      const altValue = key === 'altitude' ? value : s.altitude;
      const eccValue = key === 'eccentricity' ? value : s.eccentricity;
      const altKm = altToKm(altValue, s.altUnit);
      const ecc = parseFloat(eccValue) || 0;
      const earthRadiusKm = getEarthRadiusKm(s.altDatum);
      const perigeeRadius = altKm + earthRadiusKm;
      const smaValue = ecc > 0 ? perigeeRadius / (1 - ecc) : perigeeRadius;
      newState.sma = String(+(smaValue).toFixed(3));
    }
    set(newState as Partial<OrbitState>);
  }, [clearError, set, s.altUnit, s.angleUnit, s.orbitType, s.altitude, s.eccentricity, s.smaMode, s.smaUnit, s.sma, s.altDatum]);

  const handleToggleAlt = useCallback(() => {
    const a = toggleAlt(s.altitude, s.altUnit);
    const p = toggleAlt(s.perigee, s.altUnit);
    const ap = toggleAlt(s.apogee, s.altUnit);
    set({ altitude: a.value, altUnit: a.unit, perigee: p.value, apogee: ap.value });
  }, [s.altitude, s.perigee, s.apogee, s.altUnit, set]);

  const handleToggleSma = useCallback(() => {
    const nextUnit = s.smaUnit === 'km' ? 'm' : 'km';
    const factor = s.smaUnit === 'km' ? 1000 : 0.001;
    set({
      smaUnit: nextUnit,
      sma: String(+((parseFloat(s.sma) || 0) * factor).toFixed(nextUnit === 'm' ? 3 : 6)),
    });
  }, [s.smaUnit, s.sma, set]);



  const handleToggleAngle = useCallback(() => {
    const fields = ['inclination', 'raan', 'aop', 'trueAnomaly', 'longitude'] as const;
    const patch: Partial<OrbitState> = {};
    let newUnit: 'deg' | 'rad' = 'deg';
    fields.forEach(f => {
      const r = toggleAngle(s[f], s.angleUnit);
      (patch as any)[f] = r.value;
      newUnit = r.unit;
    });
    patch.angleUnit = newUnit;
    set(patch);
  }, [s, set]);

  const handleTogglePositionUnit = useCallback(() => {
    const nextUnit = s.posUnit === 'km' ? 'm' : 'km';
    const factor = s.posUnit === 'km' ? 1000 : 0.001;
    set({
      posUnit: nextUnit,
      posX: String(+((parseFloat(s.posX) || 0) * factor).toFixed(nextUnit === 'm' ? 3 : 6)),
      posY: String(+((parseFloat(s.posY) || 0) * factor).toFixed(nextUnit === 'm' ? 3 : 6)),
      posZ: String(+((parseFloat(s.posZ) || 0) * factor).toFixed(nextUnit === 'm' ? 3 : 6)),
    });
  }, [s.posUnit, s.posX, s.posY, s.posZ, set]);

  const handleToggleVelocityUnit = useCallback(() => {
    const nextUnit = s.velUnit === 'km/s' ? 'm/s' : 'km/s';
    const factor = s.velUnit === 'km/s' ? 1000 : 0.001;
    set({
      velUnit: nextUnit,
      velX: String(+((parseFloat(s.velX) || 0) * factor).toFixed(nextUnit === 'm/s' ? 3 : 6)),
      velY: String(+((parseFloat(s.velY) || 0) * factor).toFixed(nextUnit === 'm/s' ? 3 : 6)),
      velZ: String(+((parseFloat(s.velZ) || 0) * factor).toFixed(nextUnit === 'm/s' ? 3 : 6)),
    });
  }, [s.velUnit, s.velX, s.velY, s.velZ, set]);

  useEffect(() => {
    if (s.orbitType === 'sso') {
      const altKm = altToKm(s.altitude, s.altUnit);
      if (!isNaN(altKm)) {
        const computed = getSSOInclination(altKm, s.angleUnit);
        if (computed && computed !== s.inclination) {
          set({ inclination: computed });
        }
      }
    }
  }, [s.orbitType, s.altitude, s.altUnit, s.angleUnit, s.inclination, set]);

  return (
    <>
      {/* Section header */}
      <div className={styles.sectionDividerRow}>
        <div className={styles.sectionDivider}>ORBITAL DETAILS</div>
        {allowInputType && (
          <div className={styles.inputTypeSelect}>
            <SelectInput
              value={s.inputType}
              options={INPUT_OPTS}
              dropdownAlign="right"
              dropdownWidth={138}
              onChange={v => {
                const nextType = v as InputType;
                const conversionPatch = convertOrbitState(s, nextType);
                set(conversionPatch);
              }}
            />
          </div>
        )}
      </div>

      {/* Epoch */}
      <div className={`${styles.fieldStack} ${styles.epochFieldStack}`}>
        <FieldLabel title="Epoch">Epoch</FieldLabel>
        <div className={`${styles.fieldInput} ${s.epochError ? styles.fieldInputError : ''}`}>
          <UtcDateTimeField
            value={s.epoch}
            onChange={(display) => {
              clearError?.('epoch');
              clearError?.('orbit');
              let epochError = validateUtcDisplay(display);
              if (!epochError && display.trim()) {
                const suffix = s.epochTz === 'IST' ? '+05:30' : 'Z';
                const epochDate = new Date(display.replace(' ', 'T') + suffix);
                const year = epochDate.getUTCFullYear();
                if (!isNaN(year) && (year < 2000 || year > 2037)) {
                  epochError = 'Epoch year must be between 2000 and 2037';
                  showToastMessage('info', 'Epoch year must be between 2000 and 2037');
                }
              }
              set({ epoch: display, epochError });
            }}
            error={s.epochError}
          />
          <CustomToolTip title="Click to switch timezone" placement="top" className={styles.tooltipWrap}>
            <span
              className={styles.unitToggle}
              onClick={() => {
                const newTz = s.epochTz === 'UTC' ? 'IST' : 'UTC';
                const converted = convertEpoch(s.epoch, newTz);
                clearError?.('epoch');
                clearError?.('orbit');
                let epochError = validateUtcDisplay(converted);
                if (!epochError && converted.trim()) {
                  const suffix = newTz === 'IST' ? '+05:30' : 'Z';
                  const epochDate = new Date(converted.replace(' ', 'T') + suffix);
                  const year = epochDate.getUTCFullYear();
                  if (!isNaN(year) && (year < 2000 || year > 2037)) {
                    epochError = 'Epoch year must be between 2000 and 2037';
                    showToastMessage('info', 'Epoch year must be between 2000 and 2037');
                  }
                }
                set({ epoch: converted, epochTz: newTz, epochError });
              }}
            >
              {s.epochTz}
            </span>
          </CustomToolTip>
        </div>
      </div>

      {isCartesian && (
        <>
          <div className={styles.fieldStackCol}>
            <FieldLabel title="Position">Position</FieldLabel>
            <div className={styles.vectorGrid3}>
              <NumericInput value={s.posX} onChange={v => setField('posX', v)} error={hasError('position')} suffix={s.posUnit} onSuffixClick={handleTogglePositionUnit} suffixTitle="Click to toggle km/m" placeholder="x" allowNegative />
              <NumericInput value={s.posY} onChange={v => setField('posY', v)} error={hasError('position')} suffix={s.posUnit} onSuffixClick={handleTogglePositionUnit} suffixTitle="Click to toggle km/m" placeholder="y" allowNegative />
              <NumericInput value={s.posZ} onChange={v => setField('posZ', v)} error={hasError('position')} suffix={s.posUnit} onSuffixClick={handleTogglePositionUnit} suffixTitle="Click to toggle km/m" placeholder="z" allowNegative />
            </div>
          </div>

          <div className={styles.fieldStackCol}>
            <FieldLabel title="Velocity">Velocity</FieldLabel>
            <div className={styles.vectorGrid3}>
              <NumericInput value={s.velX} onChange={v => setField('velX', v)} error={hasError('velocity')} suffix={s.velUnit} onSuffixClick={handleToggleVelocityUnit} suffixTitle="Click to toggle km/s / m/s" placeholder="x" allowNegative />
              <NumericInput value={s.velY} onChange={v => setField('velY', v)} error={hasError('velocity')} suffix={s.velUnit} onSuffixClick={handleToggleVelocityUnit} suffixTitle="Click to toggle km/s / m/s" placeholder="y" allowNegative />
              <NumericInput value={s.velZ} onChange={v => setField('velZ', v)} error={hasError('velocity')} suffix={s.velUnit} onSuffixClick={handleToggleVelocityUnit} suffixTitle="Click to toggle km/s / m/s" placeholder="z" allowNegative />
            </div>
          </div>

          <div className={`${styles.fieldStack} ${styles.referenceFrameStack}`}>
            <FieldLabel title="Reference Frame">Reference Frame</FieldLabel>
            <SelectInput value={s.cartRefFrame} options={REF_FRAME_CARTESIAN} onChange={v => set({ cartRefFrame: v })} style={{ width: 138 }} dropdownWidth={138} />
          </div>
        </>
      )}

      {isEquinoctial && (
        <>
          <div className={styles.fieldStack}>
            <FieldLabel title="Semi-major Axis (SMA)">SMA</FieldLabel>
            <NumericInput
              value={s.sma}
              onChange={v => setField('sma', v)}
              error={hasError('sma')}
              suffix={s.smaUnit}
              onSuffixClick={handleToggleSma}
              suffixTitle="Click to toggle km/m"
              width={180}
            />
          </div>

          <div className={styles.fieldStack}>
            <FieldLabel title="ex component">ex</FieldLabel>
            <NumericInput
              value={s.ex}
              onChange={v => setField('ex', v)}
              error={hasError('ex')}
              placeholder="0.0"
              width={180}
            />
          </div>

          <div className={styles.fieldStack}>
            <FieldLabel title="ey component">ey</FieldLabel>
            <NumericInput
              value={s.ey}
              onChange={v => setField('ey', v)}
              error={hasError('ey')}
              placeholder="0.0"
              width={180}
            />
          </div>

          <div className={styles.fieldStack}>
            <FieldLabel title="hx component">hx</FieldLabel>
            <NumericInput
              value={s.hx}
              onChange={v => setField('hx', v)}
              error={hasError('hx')}
              placeholder="0.0"
              width={180}
            />
          </div>

          <div className={styles.fieldStack}>
            <FieldLabel title="hy component">hy</FieldLabel>
            <NumericInput
              value={s.hy}
              onChange={v => setField('hy', v)}
              error={hasError('hy')}
              placeholder="0.0"
              width={180}
            />
          </div>

          <div className={styles.fieldStack}>
            <FieldLabel title="Longitude">Longitude</FieldLabel>
            <NumericInput
              value={s.longitude}
              onChange={v => setField('longitude', v)}
              error={hasError('longitude')}
              suffix={s.angleUnit === 'rad' ? 'rad' : '°'}
              onSuffixClick={handleToggleAngle}
              suffixTitle="Click to toggle °/rad"
              width={180}
              allowNegative
            />
          </div>
        </>
      )}

      {/* Altitude — SSO / Inclined */}
      {isKeplerian && (isInclined || isSSO) && (
        <div className={styles.fieldStack}>
          <FieldLabel title="Altitude datum and value">Altitude</FieldLabel>
          <NumericInput
            value={s.altitude}
            onChange={v => {
              clearError?.('altitude');
              clearError?.('orbit');
              const num = parseFloat(v);
              if (!isNaN(num)) {
                const altKm = altToKm(v, s.altUnit);
                const isMeter = s.altUnit === 'm';
                const minAlt = isMeter ? ORBIT_LIMITS.altitudeKm.min * 1000 : ORBIT_LIMITS.altitudeKm.min;
                const maxLimitKm = s.orbitType === 'sso' ? 5974 : ORBIT_LIMITS.altitudeKm.max;
                const maxAlt = isMeter ? maxLimitKm * 1000 : maxLimitKm;
                if (altKm < 0 || altKm > maxLimitKm || (v.length >= 3 && altKm < ORBIT_LIMITS.altitudeKm.min)) {
                  showToastMessage('info', `Altitude must be between ${minAlt} and ${maxAlt} ${s.altUnit}`);
                  return;
                }
              }
              const earthRadiusKm = getEarthRadiusKm(s.altDatum);
              const perigeeRadius = altToKm(v, s.altUnit) + earthRadiusKm;
              const smaValue = s.eccentricity && parseFloat(s.eccentricity) > 0 ? perigeeRadius / (1 - parseFloat(s.eccentricity)) : perigeeRadius;
              set({ altitude: v, sma: String(+(smaValue).toFixed(3)) });
            }}
            onBlur={() => {
              if (!s.altitude.trim()) {
                const earthRadiusKm = getEarthRadiusKm(s.altDatum);
                const perigeeRadius = 500 + earthRadiusKm;
                const smaValue = s.eccentricity && parseFloat(s.eccentricity) > 0 ? perigeeRadius / (1 - parseFloat(s.eccentricity)) : perigeeRadius;
                set({ altitude: '500', sma: String(+(smaValue).toFixed(3)) });
                showToastMessage('info', 'Altitude is required, resetting to 500 km');
              }
            }}
            error={hasError('altitude')}
            leftChip={s.altDatum ?? 'MSL'}
            onLeftChipClick={() => set({ altDatum: s.altDatum === 'MSL' ? 'WGS84' : 'MSL' })}
            leftChipTitle="Click to toggle MSL / WGS84"
            suffix={altLabel}
            onSuffixClick={handleToggleAlt}
            suffixTitle="Click to toggle km/m"
            width={180}
          />
        </div>
      )}

      {/* LTAN — SSO only */}
      {isKeplerian && isSSO && (
        <div className={styles.fieldStack}>
          <FieldLabel title="Local Time of Ascending Node">LTAN</FieldLabel>
          <div className={`${styles.fieldInput} ${hasError('ltan') ? styles.fieldInputError : ''}`} style={{ width: 102 }}>
            <input value={s.ltan} onChange={e => setField('ltan', e.target.value)} placeholder="HH:MM:SS" />
          </div>
        </div>
      )}

      {/* Longitude — GEO only */}
      {isKeplerian && isGEO && (
        <div className={styles.fieldStack}>
          <FieldLabel title="Longitude">Longitude position</FieldLabel>
          <NumericInput
            value={s.longitude}
            onChange={v => setField('longitude', v)}
            error={hasError('longitude')}
            suffix={angleLabel}
            onSuffixClick={handleToggleAngle}
            suffixTitle="Click to toggle °/rad"
            width={102}
          />
        </div>
      )}

      {/* Altitude — GEO, generated */}
      {isKeplerian && isGEO && (
        <div className={`${styles.fieldStack} ${styles.fieldDisabled}`}>
          <FieldLabel title="Altitude generated from GEO orbit">
            Altitude
          </FieldLabel>
          <NumericInput
            value={s.altUnit === 'km' ? '35786' : '35786000'}
            onChange={() => { }}
            disabled
            leftChip={s.altDatum ?? 'MSL'}
            suffix={altLabel}
            width={180}
          />
        </div>
      )}

      {/* Perigee + Apogee — HEO */}
      {isKeplerian && isHEO && (
        <>
          <div className={styles.fieldStack}>
            <FieldLabel title="Perigee Distance">Perigee Distance</FieldLabel>
            <NumericInput
              value={s.perigee}
              onChange={v => setField('perigee', v)}
              error={hasError('perigee')}
              suffix={altLabel}
              onSuffixClick={handleToggleAlt}
              suffixTitle="Click to toggle km/m"
              width={138}
            />
          </div>
          <div className={styles.fieldStack}>
            <FieldLabel title="Apogee Distance">Apogee Distance</FieldLabel>
            <NumericInput
              value={s.apogee}
              onChange={v => setField('apogee', v)}
              error={hasError('apogee')}
              suffix={altLabel}
              onSuffixClick={handleToggleAlt}
              suffixTitle="Click to toggle km/m"
              width={138}
            />
          </div>
        </>
      )}

      {/* Eccentricity — Inclined editable */}
      {isKeplerian && isInclined && (
        <div className={styles.fieldStack}>
          <FieldLabel title="Eccentricity">Eccentricity (e)</FieldLabel>
          <NumericInput
            value={s.eccentricity}
            onChange={v => {
              const num = parseFloat(v);
              if (v.trim() !== '' && !isNaN(num)) {
                const R_Earth = 6378;
                const h_perigee_min = 150;
                const altKm = s.smaMode === 'sma'
                  ? (s.smaUnit === 'm' ? (parseFloat(s.sma) || 0) / 1000 : (parseFloat(s.sma) || 0)) - R_Earth
                  : altToKm(s.altitude || '500', s.altUnit);
                const a = R_Earth + altKm;
                const r_p_min = R_Earth + h_perigee_min;
                const e_max = (a - r_p_min) / a;

                if (num < 0) {
                  showToastMessage('info', 'Eccentricity cannot be negative');
                  return;
                }
                if (num >= 1) {
                  showToastMessage('info', 'Eccentricity must be < 1 for a closed orbit (e≥1 is parabolic/hyperbolic)');
                  return;
                }
                if (num > e_max) {
                  showToastMessage('info', `Max allowed eccentricity for ${parseFloat(altKm.toFixed(6))} km altitude is ${parseFloat(e_max.toFixed(6))}`);
                  return;
                }
              }
              const ecc = parseFloat(v) || 0;
              const altKm = altToKm(s.altitude, s.altUnit);
              const earthRadiusKm = getEarthRadiusKm(s.altDatum);
              const perigeeRadius = altKm + earthRadiusKm;
              const smaValue = ecc > 0 ? perigeeRadius / (1 - ecc) : perigeeRadius;
              set({ eccentricity: v, sma: String(+(smaValue).toFixed(3)) });
            }}
            onBlur={() => {
              if (!s.eccentricity.trim()) {
                setField('eccentricity', '0');
                showToastMessage('info', 'Eccentricity is required, resetting to 0');
              }
            }}
            error={hasError('eccentricity')}
            width={102}
          />
        </div>
      )}

      {/* Eccentricity — SSO / GEO fixed at 0 */}
      {isKeplerian && (isSSO || isGEO) && (
        <div className={`${styles.fieldStack} ${styles.fieldDisabled}`}>
          <FieldLabel title="Eccentricity fixed at 0">
            Eccentricity (e)
          </FieldLabel>
          <NumericInput value="0" onChange={() => { }} disabled width={102} />
        </div>
      )}

      {/* Inclination — all orbit types */}
      {isKeplerian && (isInclined || isSSO || isGEO || isHEO) && (
        <div className={`${styles.fieldStack} ${(isGEO || isSSO) ? styles.fieldDisabled : ''}`}>
          <FieldLabel title={isGEO ? "Inclination fixed for GEO orbit" : isSSO ? "Inclination dynamically calculated for SSO orbit" : "Inclination"}>Inclination (i)</FieldLabel>
          <NumericInput
            value={s.inclination}
            onChange={v => setField('inclination', v)}
            onBlur={() => {
              if (!s.inclination.trim()) {
                setField('inclination', '37');
                showToastMessage('info', 'Inclination is required, resetting to 37°');
              }
            }}
            error={hasError('inclination')}
            disabled={isGEO || isSSO}
            suffix={angleLabel}
            onSuffixClick={handleToggleAngle}
            suffixTitle="Click to toggle °/rad"
            width={102}
          />
        </div>
      )}

      {/* RAAN — Inclined + HEO editable */}
      {isKeplerian && (isInclined || isHEO) && (
        <div className={styles.fieldStack}>
          <FieldLabel title="Right Ascension of the Ascending Node">RAAN (Ω)</FieldLabel>
          <NumericInput
            value={s.raan}
            onChange={v => setField('raan', v)}
            onBlur={() => {
              if (!s.raan.trim()) {
                setField('raan', '0');
                showToastMessage('info', 'RAAN is required, resetting to 0°');
              }
            }}
            error={hasError('raan')}
            suffix={angleLabel}
            onSuffixClick={handleToggleAngle}
            suffixTitle="Click to toggle °/rad"
            width={102}
          />
        </div>
      )}

      {/* RAAN — SSO derived from LTAN */}
      {isKeplerian && isSSO && (
        <div className={`${styles.fieldStack} ${styles.fieldDisabled}`}>
          <FieldLabel title="Right Ascension of the Ascending Node, derived from LTAN">
            RAAN (Ω)
          </FieldLabel>
          <NumericInput value="auto" onChange={() => { }} disabled width={102} />
        </div>
      )}

      {/* Argument of Periapsis */}
      {isKeplerian && (isInclined || isHEO) && (
        <div className={`${styles.fieldStack} ${isHEO ? styles.fieldDisabled : ''}`}>
          <FieldLabel title="Argument of Periapsis">AOP (ω)</FieldLabel>
          <NumericInput
            value={isHEO ? '270' : s.aop}
            onChange={v => setField('aop', v)}
            onBlur={() => {
              if (!s.aop.trim()) {
                setField('aop', '0');
                showToastMessage('info', 'AOP is required, resetting to 0°');
              }
            }}
            error={hasError('aop')}
            disabled={isHEO}
            suffix={angleLabel}
            onSuffixClick={handleToggleAngle}
            suffixTitle="Click to toggle °/rad"
            width={102}
          />
        </div>
      )}

      {/* True Anomaly — Inclined only */}
      {isKeplerian && isInclined && (
        <div className={styles.fieldStack}>
          <FieldLabel title="True Anomaly">TA (θ)</FieldLabel>
          <NumericInput
            value={s.trueAnomaly}
            onChange={v => setField('trueAnomaly', v)}
            onBlur={() => {
              if (!s.trueAnomaly.trim()) {
                setField('trueAnomaly', '0');
                showToastMessage('info', 'TA is required, resetting to 0°');
              }
            }}
            error={hasError('trueAnomaly')}
            suffix={angleLabel}
            onSuffixClick={handleToggleAngle}
            suffixTitle="Click to toggle °/rad"
            width={102}
          />
        </div>
      )}

      {/* Reference Frame — all Keplerian orbit types */}
      {isKeplerian && (
        <div className={`${styles.fieldStack} ${styles.referenceFrameStack}`}>
          <FieldLabel title="Reference Frame">Reference Frame</FieldLabel>
          <SelectInput value={s.refFrame} options={REF_FRAME_KEPLERIAN} onChange={v => set({ refFrame: v })} style={{ width: 102 }} dropdownWidth={102} />
        </div>
      )}
    </>
  );
};

// ── Helper: Convert OrbitState to KeplerianOrbitParams ─────────────────────────

const EARTH_RADIUS_KM = 6371;
const WGS84_EARTH_RADIUS_KM = 6378.14;
const GEO_SMA_KM = 42164;
const EARTH_MU_KM3_S2 = 398600.4418;
const EPS = 1e-8;

function getEarthRadiusKm(altDatum: 'MSL' | 'WGS84' | undefined): number {
  return altDatum === 'WGS84' ? WGS84_EARTH_RADIUS_KM : EARTH_RADIUS_KM;
}

const sanitizeInclination = (inc: number): number => {
  if (Math.abs(inc - 180.0) < 1e-6) {
    return 179.99;
  }
  return inc;
};

const normalizeAngle = (val: number): number => {
  return ((val % 360) + 360) % 360;
};

const ORBIT_LIMITS = {
  altitudeKm: { min: 100, max: 50000 },
  smaKm: { min: EARTH_RADIUS_KM + 100, max: 50000 + EARTH_RADIUS_KM },
  eccentricity: { min: 0, max: 0.999 },
  inclinationDeg: { min: 0, max: 180 },
  ssoInclinationDeg: { min: 90, max: 110 },
  orbitalAngleDeg: { min: 0, max: 360 },
  longitudeDeg: { min: -180, max: 180 },
  cartesianRadiusKm: { min: EARTH_RADIUS_KM + 100, max: 50000 + EARTH_RADIUS_KM },
  cartesianSpeedKmS: { minExclusive: 0, max: 20 },
  hMagnitudeKm2S: { minExclusive: 0, max: 1_000_000 },
  eVectorMagnitude: { min: 0, maxExclusive: 1 },
  groupSatellites: { min: 1, max: 500 },
  groupPlanes: { min: 1, max: 100 },
  groupAngleDeg: { min: 0, max: 360 },
} as const;

const orbitTypePatch = (orbitType: OrbitType, s: OrbitState): Partial<OrbitState> => {
  const baseOrbitPatch: Partial<OrbitState> = { orbitType };

  if (orbitType === 'sso') {
    baseOrbitPatch.eccentricity = '0';
    const altKm = altToKm(s.altitude, s.altUnit);
    const earthRadiusKm = getEarthRadiusKm(s.altDatum);
    if (altKm > 5974) {
      const isMeter = s.altUnit === 'm';
      baseOrbitPatch.altitude = isMeter ? String(5974 * 1000) : '5974';
      baseOrbitPatch.sma = String(+(5974 + earthRadiusKm).toFixed(3));
      baseOrbitPatch.inclination = getSSOInclination(5974, s.angleUnit);
    } else {
      baseOrbitPatch.inclination = getSSOInclination(altKm, s.angleUnit);
    }
  } else if (orbitType === 'geo') {
    baseOrbitPatch.inclination = '0';
    baseOrbitPatch.eccentricity = '0';
  } else if (orbitType === 'heo') {
    baseOrbitPatch.inclination = '63.4';
    baseOrbitPatch.aop = '270';
  }

  // Force Keplerian input type for non-inclined orbits
  if (orbitType !== 'inclined') {
    return { ...baseOrbitPatch, inputType: 'keplerian' as InputType };
  }
  return baseOrbitPatch;
};

type Vec3 = [number, number, number];

function angleConvert(val: string, from: 'deg' | 'rad'): number {
  const n = parseFloat(val) || 0;
  return from === 'deg' ? n : n * 180 / Math.PI;
}

function altToKm(val: string, unit: 'km' | 'm'): number {
  const n = parseFloat(val) || 0;
  return unit === 'km' ? n : n / 1000;
}

function getSSOInclination(altitudeKm: number, angleUnit: 'deg' | 'rad'): string {
  const aKm = altitudeKm + 6378.137;
  let cosI = -4.77351328e-15 * Math.pow(aKm, 3.5);
  cosI = Math.max(-1.0, Math.min(1.0, cosI));
  const inclinationRad = Math.acos(cosI);
  const inclinationDeg = (inclinationRad * 180) / Math.PI;
  if (inclinationDeg < 0.0 || inclinationDeg > 180.0) {
    return '';
  }
  return angleUnit === 'rad' ? inclinationRad.toFixed(6) : inclinationDeg.toFixed(4);
}

function normalizeDegrees(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

function radToDeg(rad: number): number {
  return rad * 180 / Math.PI;
}

function vecDot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function vecCross(a: Vec3, b: Vec3): Vec3 {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

function vecScale(a: Vec3, scale: number): Vec3 {
  return [a[0] * scale, a[1] * scale, a[2] * scale];
}

function vecSub(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

function vecMag(a: Vec3): number {
  return Math.sqrt(vecDot(a, a));
}

function safeAcos(v: number): number {
  return Math.acos(Math.min(1, Math.max(-1, v)));
}

function cartesianToKeplerian(r: Vec3, v: Vec3, epoch: number): KeplerianOrbitParams | null {
  const rMag = vecMag(r);
  const vMag = vecMag(v);
  if (rMag <= EPS || vMag <= EPS) return null;

  const hVec = vecCross(r, v);
  const hMag = vecMag(hVec);
  if (hMag <= EPS) return null;

  const nVec = vecCross([0, 0, 1], hVec);
  const nMag = vecMag(nVec);
  const eVec = vecSub(vecScale(vecCross(v, hVec), 1 / EARTH_MU_KM3_S2), vecScale(r, 1 / rMag));
  let eMag = vecMag(eVec);
  const energy = (vMag * vMag) / 2 - EARTH_MU_KM3_S2 / rMag;
  if (Math.abs(energy) <= EPS) return null;

  const semiMajorAxis = -EARTH_MU_KM3_S2 / (2 * energy);
  if (!Number.isFinite(semiMajorAxis) || semiMajorAxis <= 0) return null;

  let inclination = radToDeg(safeAcos(hVec[2] / hMag));

  const TOL = 1e-5;
  const isCircular = eMag < TOL;
  const isEquatorial = inclination < TOL || Math.abs(inclination - 180) < TOL;

  if (isCircular) {
    eMag = 0;
  }

  let RAAN = 0;
  let AOP = 0;
  let TA = 0;

  if (isEquatorial && isCircular) {
    RAAN = 0;
    AOP = 0;
    TA = normalizeDegrees(radToDeg(Math.atan2(r[1], r[0])));
  } else if (isEquatorial) {
    RAAN = 0;
    AOP = normalizeDegrees(radToDeg(Math.atan2(eVec[1], eVec[0])));
    const y = vecDot(vecCross(eVec, r), hVec) / (eMag * rMag * hMag);
    const x = vecDot(eVec, r) / (eMag * rMag);
    TA = normalizeDegrees(radToDeg(Math.atan2(y, x)));
  } else if (isCircular) {
    RAAN = nMag > EPS ? normalizeDegrees(radToDeg(Math.atan2(nVec[1], nVec[0]))) : 0;
    AOP = 0;
    const y = vecDot(vecCross(nVec, r), hVec) / (nMag * rMag * hMag);
    const x = vecDot(nVec, r) / (nMag * rMag);
    TA = normalizeDegrees(radToDeg(Math.atan2(y, x)));
  } else {
    RAAN = nMag > EPS ? normalizeDegrees(radToDeg(Math.atan2(nVec[1], nVec[0]))) : 0;
    const yAop = vecDot(vecCross(nVec, eVec), hVec) / (nMag * eMag * hMag);
    const xAop = vecDot(nVec, eVec) / (nMag * eMag);
    AOP = normalizeDegrees(radToDeg(Math.atan2(yAop, xAop)));
    const yTa = vecDot(vecCross(eVec, r), hVec) / (eMag * rMag * hMag);
    const xTa = vecDot(eVec, r) / (eMag * rMag);
    TA = normalizeDegrees(radToDeg(Math.atan2(yTa, xTa)));
  }

  return {
    semiMajorAxis,
    eccentricity: eMag,
    inclination,
    RAAN,
    AOP,
    TA,
    epoch,
  };
}

function vectorPairToKeplerian(hVec: Vec3, eVec: Vec3, epoch: number): KeplerianOrbitParams | null {
  const hMag = vecMag(hVec);
  const eMag = vecMag(eVec);
  if (hMag <= EPS || eMag >= 1) return null;

  const nVec = vecCross([0, 0, 1], hVec);
  const nMag = vecMag(nVec);
  const semiMajorAxis = (hMag * hMag) / (EARTH_MU_KM3_S2 * (1 - eMag * eMag));
  if (!Number.isFinite(semiMajorAxis) || semiMajorAxis <= 0) return null;

  const inclination = radToDeg(safeAcos(hVec[2] / hMag));
  const RAAN = nMag > EPS ? normalizeDegrees(radToDeg(Math.atan2(nVec[1], nVec[0]))) : 0;
  let AOP = 0;
  if (nMag > EPS && eMag > EPS) {
    const y = vecDot(vecCross(nVec, eVec), hVec) / (nMag * eMag * hMag);
    const x = vecDot(nVec, eVec) / (nMag * eMag);
    AOP = normalizeDegrees(radToDeg(Math.atan2(y, x)));
  }

  return {
    semiMajorAxis,
    eccentricity: eMag,
    inclination,
    RAAN,
    AOP,
    TA: 0,
    epoch,
  };
}

function keplerianToCartesian(params: KeplerianOrbitParams): { r: Vec3; v: Vec3 } | null {
  const a = params.semiMajorAxis;
  const e = params.eccentricity;
  const iRad = (params.inclination * Math.PI) / 180;
  const raanRad = (params.RAAN * Math.PI) / 180;
  const aopRad = (params.AOP * Math.PI) / 180;
  const taRad = (params.TA * Math.PI) / 180;

  if (a <= 0 || e < 0 || e >= 1) return null;

  const p = a * (1 - e * e);
  const rMag = p / (1 + e * Math.cos(taRad));

  const x_pqw = rMag * Math.cos(taRad);
  const y_pqw = rMag * Math.sin(taRad);

  const sqrtMuOverP = Math.sqrt(EARTH_MU_KM3_S2 / p);
  const vx_pqw = -sqrtMuOverP * Math.sin(taRad);
  const vy_pqw = sqrtMuOverP * (e + Math.cos(taRad));

  const cosO = Math.cos(raanRad), sinO = Math.sin(raanRad);
  const cosI = Math.cos(iRad), sinI = Math.sin(iRad);
  const cosW = Math.cos(aopRad), sinW = Math.sin(aopRad);

  const r11 = cosO * cosW - sinO * sinW * cosI;
  const r12 = -cosO * sinW - sinO * cosW * cosI;
  const r21 = sinO * cosW + cosO * sinW * cosI;
  const r22 = -sinO * sinW + cosO * cosW * cosI;
  const r31 = sinW * sinI;
  const r32 = cosW * sinI;

  const r: Vec3 = [
    r11 * x_pqw + r12 * y_pqw,
    r21 * x_pqw + r22 * y_pqw,
    r31 * x_pqw + r32 * y_pqw,
  ];

  const v: Vec3 = [
    r11 * vx_pqw + r12 * vy_pqw,
    r21 * vx_pqw + r22 * vy_pqw,
    r31 * vx_pqw + r32 * vy_pqw,
  ];

  return { r, v };
}

function cartesianToEquinoctial(r: Vec3, v: Vec3): { h: Vec3; e: Vec3 } | null {
  const rMag = vecMag(r);
  if (rMag <= EPS) return null;
  const hVec = vecCross(r, v);
  const hMag = vecMag(hVec);
  if (hMag <= EPS) return null;
  const eVec = vecSub(vecScale(vecCross(v, hVec), 1 / EARTH_MU_KM3_S2), vecScale(r, 1 / rMag));
  return { h: hVec, e: eVec };
}

function getCurrentKeplerian(s: OrbitState): KeplerianOrbitParams | null {
  const earthRadiusKm = getEarthRadiusKm(s.altDatum);
  if (s.orbitType === 'inclined') {
    const altKm = altToKm(s.altitude, s.altUnit);
    const semiMajorAxis = s.smaMode === 'sma' ? (parseFloat(s.sma) || 0) : altKm + earthRadiusKm;
    return {
      semiMajorAxis,
      eccentricity: parseFloat(s.eccentricity) || 0,
      inclination: angleConvert(s.inclination, s.angleUnit),
      RAAN: angleConvert(s.raan, s.angleUnit),
      AOP: angleConvert(s.aop, s.angleUnit),
      TA: angleConvert(s.trueAnomaly, s.angleUnit),
      epoch: 0,
    };
  }
  if (s.orbitType === 'sso') {
    const altKm = altToKm(s.altitude, s.altUnit);
    const semiMajorAxis = s.smaMode === 'sma' ? (parseFloat(s.sma) || 0) : altKm + earthRadiusKm;
    const inc = angleConvert(s.inclination, s.angleUnit);
    const RAAN = ltanToRaan(s.ltan || '10:30:00', s.epoch, s.epochTz);
    return {
      semiMajorAxis,
      eccentricity: 0,
      inclination: inc,
      RAAN,
      AOP: 0,
      TA: 0,
      epoch: 0,
    };
  }
  if (s.orbitType === 'geo') {
    const lonDeg = angleConvert(s.longitude, s.angleUnit);
    return {
      semiMajorAxis: GEO_SMA_KM,
      eccentricity: 0,
      inclination: 0,
      RAAN: 0,
      AOP: 0,
      TA: lonDeg,
      epoch: 0,
    };
  }
  if (s.orbitType === 'heo') {
    const perigeeKm = altToKm(s.perigee, s.altUnit);
    const apogeeKm = altToKm(s.apogee, s.altUnit);
    const rp = perigeeKm + earthRadiusKm;
    const ra = apogeeKm + earthRadiusKm;
    const semiMajorAxis = (rp + ra) / 2;
    const eccentricity = (ra - rp) / (ra + rp);
    const inc = angleConvert(s.inclination, s.angleUnit);
    return {
      semiMajorAxis,
      eccentricity,
      inclination: inc,
      RAAN: 0,
      AOP: 270,
      TA: 0,
      epoch: 0,
    };
  }
  return null;
}

function getKeplerianFromCartesian(s: OrbitState): KeplerianOrbitParams | null {
  const posScale = s.posUnit === 'm' ? 0.001 : 1;
  const velScale = s.velUnit === 'm/s' ? 0.001 : 1;
  const r: Vec3 = [
    (parseFloat(s.posX) || 0) * posScale,
    (parseFloat(s.posY) || 0) * posScale,
    (parseFloat(s.posZ) || 0) * posScale,
  ];
  const v: Vec3 = [
    (parseFloat(s.velX) || 0) * velScale,
    (parseFloat(s.velY) || 0) * velScale,
    (parseFloat(s.velZ) || 0) * velScale,
  ];
  return cartesianToKeplerian(r, v, 0);
}

function getKeplerianFromEquinoctial(s: OrbitState): KeplerianOrbitParams | null {
  const smaKm = s.smaUnit === 'm' ? (parseFloat(s.sma) || 0) / 1000 : (parseFloat(s.sma) || 0);
  const exVal = parseFloat(s.ex) || 0;
  const eyVal = parseFloat(s.ey) || 0;
  const hxVal = parseFloat(s.hx) || 0;
  const hyVal = parseFloat(s.hy) || 0;
  const lonDeg = angleConvert(s.longitude, s.angleUnit);

  const hMag = Math.hypot(hxVal, hyVal);
  const inclination = radToDeg(2 * Math.atan(hMag));
  const RAAN = hMag > EPS ? normalizeDegrees(radToDeg(Math.atan2(hxVal, hyVal))) : 0;

  const eMag = Math.hypot(exVal, eyVal);
  if (eMag >= 1) return null;

  const aopPlusRaan = (exVal === 0 && eyVal === 0) ? 0 : normalizeDegrees(radToDeg(Math.atan2(eyVal, exVal)));
  const AOP = normalizeDegrees(aopPlusRaan - RAAN);
  const TA = normalizeDegrees(lonDeg - aopPlusRaan);

  return {
    semiMajorAxis: smaKm,
    eccentricity: eMag,
    inclination,
    RAAN,
    AOP,
    TA,
    epoch: 0,
  };
}

function getCurrentAsKeplerian(s: OrbitState): KeplerianOrbitParams | null {
  if (s.inputType === 'keplerian') {
    return getCurrentKeplerian(s);
  } else if (s.inputType === 'cartesian') {
    return getKeplerianFromCartesian(s);
  } else if (s.inputType === 'equinoctial') {
    return getKeplerianFromEquinoctial(s);
  }
  return null;
}

function keplerianToCartesianPatch(kep: KeplerianOrbitParams, targetPosUnit: 'km' | 'm', targetVelUnit: 'km/s' | 'm/s'): Partial<OrbitState> | null {
  const cart = keplerianToCartesian(kep);
  if (!cart) return null;

  const posFactor = targetPosUnit === 'm' ? 1000 : 1;
  const velFactor = targetVelUnit === 'm/s' ? 1000 : 1;

  const posX = (cart.r[0] * posFactor).toFixed(targetPosUnit === 'm' ? 3 : 6);
  const posY = (cart.r[1] * posFactor).toFixed(targetPosUnit === 'm' ? 3 : 6);
  const posZ = (cart.r[2] * posFactor).toFixed(targetPosUnit === 'm' ? 3 : 6);

  const velX = (cart.v[0] * velFactor).toFixed(targetVelUnit === 'm/s' ? 3 : 6);
  const velY = (cart.v[1] * velFactor).toFixed(targetVelUnit === 'm/s' ? 3 : 6);
  const velZ = (cart.v[2] * velFactor).toFixed(targetVelUnit === 'm/s' ? 3 : 6);

  return { posX, posY, posZ, velX, velY, velZ };
}

function keplerianToEquinoctialPatch(
  kep: KeplerianOrbitParams,
  targetSmaUnit: 'km' | 'm',
  targetAngleUnit: 'deg' | 'rad',
): Partial<OrbitState> | null {
  const smaFactor = targetSmaUnit === 'm' ? 1000 : 1;
  const sma = (kep.semiMajorAxis * smaFactor).toFixed(targetSmaUnit === 'm' ? 3 : 6);

  const incRad = (kep.inclination * Math.PI) / 180;
  const raanRad = (kep.RAAN * Math.PI) / 180;
  const aopRad = (kep.AOP * Math.PI) / 180;

  // Inclination elements h, k (hx, hy)
  const hx = (Math.tan(incRad / 2) * Math.sin(raanRad)).toFixed(8);
  const hy = (Math.tan(incRad / 2) * Math.cos(raanRad)).toFixed(8);

  // Eccentricity elements ex, ey
  const ex = (kep.eccentricity * Math.cos(aopRad + raanRad)).toFixed(8);
  const ey = (kep.eccentricity * Math.sin(aopRad + raanRad)).toFixed(8);

  const lonDeg = normalizeDegrees(kep.RAAN + kep.AOP + kep.TA);
  const longitude = (targetAngleUnit === 'rad' ? (lonDeg * Math.PI) / 180 : lonDeg).toFixed(targetAngleUnit === 'rad' ? 8 : 6);

  return { sma, ex, ey, hx, hy, longitude };
}

function formatNumber(val: number, precision = 6): string {
  const factor = Math.pow(10, precision);
  const rounded = Math.round(val * factor) / factor;
  if (Math.abs(rounded - Math.round(rounded)) < 1e-5) {
    return String(Math.round(rounded));
  }
  let str = rounded.toFixed(precision);
  if (str.indexOf('.') !== -1) {
    str = str.replace(/\.?0+$/, '');
  }
  return str;
}

function keplerianToKeplerianPatch(kep: KeplerianOrbitParams, s: OrbitState): Partial<OrbitState> {
  const isAltM = s.altUnit === 'm';
  const isAngleRad = s.angleUnit === 'rad';
  const earthRadiusKm = getEarthRadiusKm(s.altDatum);

  const altKm = kep.semiMajorAxis - earthRadiusKm;
  const altitude = isAltM ? formatNumber(altKm * 1000, 3) : formatNumber(altKm, 6);
  const sma = isAltM ? formatNumber(kep.semiMajorAxis * 1000, 3) : formatNumber(kep.semiMajorAxis, 6);

  const degToRad = Math.PI / 180;
  const inclination = isAngleRad ? formatNumber(kep.inclination * degToRad, 8) : formatNumber(kep.inclination, 6);
  const raan = isAngleRad ? formatNumber(kep.RAAN * degToRad, 8) : formatNumber(kep.RAAN, 6);
  const aop = isAngleRad ? formatNumber(kep.AOP * degToRad, 8) : formatNumber(kep.AOP, 6);
  const trueAnomaly = isAngleRad ? formatNumber(kep.TA * degToRad, 8) : formatNumber(kep.TA, 6);

  const eccentricity = formatNumber(kep.eccentricity, 8);

  const rp = kep.semiMajorAxis * (1 - kep.eccentricity) - earthRadiusKm;
  const ra = kep.semiMajorAxis * (1 + kep.eccentricity) - earthRadiusKm;
  const perigee = isAltM ? formatNumber(rp * 1000, 3) : formatNumber(rp, 6);
  const apogee = isAltM ? formatNumber(ra * 1000, 3) : formatNumber(ra, 6);

  const longitude = isAngleRad ? formatNumber(kep.TA * degToRad, 8) : formatNumber(kep.TA, 6);

  return {
    altitude,
    sma,
    eccentricity,
    inclination,
    raan,
    aop,
    trueAnomaly,
    perigee,
    apogee,
    longitude,
  };
}

function convertOrbitState(s: OrbitState, nextInputType: InputType): Partial<OrbitState> {
  const kep = getCurrentAsKeplerian(s);
  if (!kep) {
    return {
      inputType: nextInputType,
      ...(nextInputType !== 'keplerian' ? { orbitType: 'inclined' as OrbitType } : {}),
    };
  }

  let patch: Partial<OrbitState> = {};
  if (nextInputType === 'keplerian') {
    patch = keplerianToKeplerianPatch(kep, s);
  } else if (nextInputType === 'cartesian') {
    const cartPatch = keplerianToCartesianPatch(kep, s.posUnit, s.velUnit);
    if (cartPatch) patch = cartPatch;
  } else if (nextInputType === 'equinoctial') {
    const eqPatch = keplerianToEquinoctialPatch(kep, s.smaUnit, s.angleUnit);
    if (eqPatch) patch = eqPatch;
  }

  return {
    ...patch,
    inputType: nextInputType,
    ...(nextInputType !== 'keplerian' ? { orbitType: 'inclined' as OrbitType } : {}),
  };
}

function extractEphemeris(raw: any): any[] | undefined {
  const src = raw?.ephemeris ?? raw?.propagatedEphemeris ?? raw?.states ?? raw?.stateVectors;
  if (!Array.isArray(src) || src.length === 0) return undefined;
  const ephemeris = src.map((p: any) => ({
    epoch: p.epoch ?? p.time ?? p.timestamp ?? 0,
    x: p.x ?? p.position?.x ?? p.r?.[0] ?? 0,
    y: p.y ?? p.position?.y ?? p.r?.[1] ?? 0,
    z: p.z ?? p.position?.z ?? p.r?.[2] ?? 0,
    vx: p.vx ?? p.velocity?.x ?? p.v?.[0],
    vy: p.vy ?? p.velocity?.y ?? p.v?.[1],
    vz: p.vz ?? p.velocity?.z ?? p.v?.[2],
  })).filter((p: any) => p.epoch && Number.isFinite(Number(p.x)) && Number.isFinite(Number(p.y)) && Number.isFinite(Number(p.z)));
  return ephemeris.length > 0 ? ephemeris : undefined;
}

function unwrapCreateResponse(res: any): any {
  return res?.data?.data ?? res?.data ?? res;
}

function getSunRtAscension(epochDate: Date): number {
  const j2000 = Date.UTC(2000, 0, 1, 12, 0, 0);
  const diffDays = (epochDate.getTime() - j2000) / 86400000;
  let L = (280.460 + 0.9856474 * diffDays) % 360;
  if (L < 0) L += 360;
  let g = (357.528 + 0.9856003 * diffDays) % 360;
  if (g < 0) g += 360;
  const g_rad = g * Math.PI / 180;
  const lambda = L + 1.915 * Math.sin(g_rad) + 0.020 * Math.sin(2 * g_rad);
  const lambda_rad = lambda * Math.PI / 180;
  const epsilon_rad = 23.439 * Math.PI / 180;
  const ra_rad = Math.atan2(Math.cos(epsilon_rad) * Math.sin(lambda_rad), Math.cos(lambda_rad));
  let ra_deg = ra_rad * 180 / Math.PI;
  if (ra_deg < 0) ra_deg += 360;
  return ra_deg;
}

/** Precise RAAN for SSO from LTAN (HH:MM:SS) and epoch string (YYYY-MM-DD HH:MM:SS). */
function ltanToRaan(ltan: string, epoch: string, epochTz: 'UTC' | 'IST' = 'UTC'): number {
  const suffix = epochTz === 'IST' ? '+05:30' : 'Z';
  const epochDate = new Date(epoch.replace(' ', 'T') + suffix);
  if (isNaN(epochDate.getTime())) return 0;
  const sunRaDeg = getSunRtAscension(epochDate);
  const parts = ltan.split(':').map(Number);
  const ltanHours = (parts[0] || 0) + (parts[1] || 0) / 60 + (parts[2] || 0) / 3600;
  return ((ltanHours - 12) * 15 + sunRaDeg + 720) % 360;
}

function orbitStateToKeplerian(s: OrbitState): KeplerianOrbitParams | null {
  if (!s.epoch || validateUtcDisplay(s.epoch)) return null;
  const suffix = s.epochTz === 'IST' ? '+05:30' : 'Z';
  const epochSec = new Date(s.epoch.replace(' ', 'T') + suffix).getTime() / 1000;
  if (isNaN(epochSec)) return null;
  const earthRadiusKm = getEarthRadiusKm(s.altDatum);

  if (s.inputType === 'cartesian') {
    const posScale = s.posUnit === 'm' ? 0.001 : 1;
    const velScale = s.velUnit === 'm/s' ? 0.001 : 1;
    const r: Vec3 = [
      (parseFloat(s.posX) || 0) * posScale,
      (parseFloat(s.posY) || 0) * posScale,
      (parseFloat(s.posZ) || 0) * posScale,
    ];
    const v: Vec3 = [
      (parseFloat(s.velX) || 0) * velScale,
      (parseFloat(s.velY) || 0) * velScale,
      (parseFloat(s.velZ) || 0) * velScale,
    ];
    return cartesianToKeplerian(r, v, epochSec);
  }

  if (s.inputType === 'equinoctial') {
    const kep = getKeplerianFromEquinoctial(s);
    if (kep) kep.epoch = epochSec;
    return kep;
  }

  if (s.orbitType === 'inclined') {
    const altKm = altToKm(s.altitude, s.altUnit);
    const ecc = parseFloat(s.eccentricity) || 0;
    const perigeeRadius = altKm + earthRadiusKm;
    const semiMajorAxis = s.smaMode === 'sma' ? (parseFloat(s.sma) || 0) : (ecc > 0 ? perigeeRadius / (1 - ecc) : perigeeRadius);
    if (isNaN(semiMajorAxis) || semiMajorAxis <= 0) return null;
    return {
      semiMajorAxis,
      eccentricity: ecc,
      inclination: sanitizeInclination(angleConvert(s.inclination, s.angleUnit)),
      RAAN: normalizeAngle(angleConvert(s.raan, s.angleUnit)),
      AOP: normalizeAngle(angleConvert(s.aop, s.angleUnit)),
      TA: normalizeAngle(angleConvert(s.trueAnomaly, s.angleUnit)),
      epoch: epochSec,
    };
  }

  if (s.orbitType === 'sso') {
    const altKm = altToKm(s.altitude, s.altUnit);
    const ecc = parseFloat(s.eccentricity) || 0;
    const perigeeRadius = altKm + earthRadiusKm;
    const semiMajorAxis = s.smaMode === 'sma' ? (parseFloat(s.sma) || 0) : (ecc > 0 ? perigeeRadius / (1 - ecc) : perigeeRadius);
    if (isNaN(semiMajorAxis) || semiMajorAxis <= 0) return null;
    const inc = sanitizeInclination(angleConvert(s.inclination, s.angleUnit));
    const RAAN = normalizeAngle(ltanToRaan(s.ltan || '10:30:00', s.epoch, s.epochTz));
    return {
      semiMajorAxis,
      eccentricity: ecc,
      inclination: inc,
      RAAN,
      AOP: 0,
      TA: 0,
      epoch: epochSec,
    };
  }

  if (s.orbitType === 'geo') {
    const lonDeg = angleConvert(s.longitude, s.angleUnit);
    return {
      semiMajorAxis: GEO_SMA_KM,
      eccentricity: 0,
      inclination: 0,
      RAAN: 0,
      AOP: 0,
      TA: lonDeg,
      epoch: epochSec,
    };
  }

  if (s.orbitType === 'heo') {
    const perigeeKm = altToKm(s.perigee, s.altUnit);
    const apogeeKm = altToKm(s.apogee, s.altUnit);
    const rp = perigeeKm + earthRadiusKm;
    const ra = apogeeKm + earthRadiusKm;
    if (ra <= rp || rp <= 0) return null;
    const semiMajorAxis = (rp + ra) / 2;
    const eccentricity = (ra - rp) / (ra + rp);
    const inc = sanitizeInclination(angleConvert(s.inclination, s.angleUnit));
    return {
      semiMajorAxis,
      eccentricity,
      inclination: inc,
      RAAN: 0,
      AOP: 270,
      TA: 0,
      epoch: epochSec,
    };
  }

  return null;
}

function validateOrbitState(s: OrbitState, modelData?: ModelData | null): ValidationErrors {
  const errors: ValidationErrors = {};
  const earthRadiusKm = getEarthRadiusKm(s.altDatum);
  const epochError = validateUtcDisplay(s.epoch);
  if (!s.epoch.trim()) errors.epoch = 'Epoch date and time is required';
  else if (epochError) errors.epoch = epochError;
  else {
    const suffix = s.epochTz === 'IST' ? '+05:30' : 'Z';
    const epochDate = new Date(s.epoch.replace(' ', 'T') + suffix);
    const year = epochDate.getUTCFullYear();
    if (isNaN(year) || year < 2000 || year > 2037) {
      errors.epoch = 'Epoch year must be between 2000 and 2037';
    }
  }

  const angleDeg = (key: keyof OrbitState) => {
    const value = parseFiniteNumber(String(s[key]));
    return value === null ? null : angleConvert(String(s[key]), s.angleUnit);
  };
  const requireAngle = (
    key: keyof OrbitState,
    label: string,
    min: number = ORBIT_LIMITS.orbitalAngleDeg.min,
    max: number = ORBIT_LIMITS.orbitalAngleDeg.max,
  ) => {
    const value = angleDeg(key);
    if (value === null) errors[key] = `${label} is required`;
    else if (value < min || value > max) {
      const isRad = s.angleUnit === 'rad';
      const displayMin = isRad ? +(min * Math.PI / 180).toFixed(4) : min;
      const displayMax = isRad ? +(max * Math.PI / 180).toFixed(4) : max;
      const unitSymbol = isRad ? ' rad' : '°';
      errors[key] = `${label} must be a number between ${displayMin} and ${displayMax}${unitSymbol}`;
    }
  };
  const requireAltitude = (value: string, key: keyof OrbitState, label: string) => {
    const parsed = parseFiniteNumber(value);
    const altitude = altToKm(value, s.altUnit);
    if (parsed === null) errors[key] = `${label} is required`;
    else if (!inRange(altitude, ORBIT_LIMITS.altitudeKm.min, ORBIT_LIMITS.altitudeKm.max)) {
      const isMeter = s.altUnit === 'm';
      const minAlt = isMeter ? ORBIT_LIMITS.altitudeKm.min * 1000 : ORBIT_LIMITS.altitudeKm.min;
      const maxAlt = isMeter ? ORBIT_LIMITS.altitudeKm.max * 1000 : ORBIT_LIMITS.altitudeKm.max;
      errors[key] = `${label} must be a number between ${minAlt} and ${maxAlt} ${s.altUnit}`;
    }
  };
  const requireSma = () => {
    const sma = parseFiniteNumber(s.sma);
    if (sma === null) errors.sma = 'Semi-major Axis (SMA) is required';
    else {
      const smaKm = s.smaUnit === 'm' ? sma / 1000 : sma;
      if (!inRange(smaKm, ORBIT_LIMITS.smaKm.min, ORBIT_LIMITS.smaKm.max)) {
        const isMeter = s.smaUnit === 'm';
        const minSma = isMeter ? ORBIT_LIMITS.smaKm.min * 1000 : ORBIT_LIMITS.smaKm.min;
        const maxSma = isMeter ? ORBIT_LIMITS.smaKm.max * 1000 : ORBIT_LIMITS.smaKm.max;
        errors.sma = `Semi-major Axis (SMA) must be a number between ${minSma} and ${maxSma} ${s.smaUnit}`;
      }
    }
  };

  if (s.inputType === 'cartesian') {
    const posScale = s.posUnit === 'm' ? 0.001 : 1;
    const velScale = s.velUnit === 'm/s' ? 0.001 : 1;
    const pos = [s.posX, s.posY, s.posZ].map(parseFiniteNumber);
    const vel = [s.velX, s.velY, s.velZ].map(parseFiniteNumber);
    if (pos.some(v => v === null)) errors.position = 'All position coordinates (x, y, z) are required';
    else {
      const radiusKm = Math.hypot(...(pos as number[]).map(v => v * posScale));
      if (!inRange(radiusKm, ORBIT_LIMITS.cartesianRadiusKm.min, ORBIT_LIMITS.cartesianRadiusKm.max)) {
        errors.position = `Position radius must be a number between ${ORBIT_LIMITS.cartesianRadiusKm.min} and ${ORBIT_LIMITS.cartesianRadiusKm.max} km`;
      }
    }
    if (vel.some(v => v === null)) errors.velocity = 'All velocity coordinates (x, y, z) are required';
    else {
      const speedKmS = Math.hypot(...(vel as number[]).map(v => v * velScale));
      if (speedKmS <= ORBIT_LIMITS.cartesianSpeedKmS.minExclusive || speedKmS > ORBIT_LIMITS.cartesianSpeedKmS.max) {
        errors.velocity = `Velocity magnitude must be a number greater than ${ORBIT_LIMITS.cartesianSpeedKmS.minExclusive} and up to ${ORBIT_LIMITS.cartesianSpeedKmS.max} km/s`;
      }
    }

    if (!errors.position && !errors.velocity) {
      const r: Vec3 = [
        (pos[0] as number) * posScale,
        (pos[1] as number) * posScale,
        (pos[2] as number) * posScale,
      ];
      const v: Vec3 = [
        (vel[0] as number) * velScale,
        (vel[1] as number) * velScale,
        (vel[2] as number) * velScale,
      ];
      const kep = cartesianToKeplerian(r, v, 0);
      if (!kep) {
        errors.velocity = 'Position/velocity combination results in an unbound/escape orbit (eccentricity >= 1)';
      } else {
        const rp = kep.semiMajorAxis * (1 - kep.eccentricity);
        const periapsisAltKm = rp - earthRadiusKm;
        if (periapsisAltKm < ORBIT_LIMITS.altitudeKm.min) {
          errors.position = `Orbit intersects the Earth (periapsis altitude ${periapsisAltKm.toFixed(1)} km is less than ${ORBIT_LIMITS.altitudeKm.min} km)`;
        }
      }
    }
  } else if (s.inputType === 'equinoctial') {
    requireSma();

    const exVal = parseFiniteNumber(s.ex);
    const eyVal = parseFiniteNumber(s.ey);
    if (exVal === null) errors.ex = 'ex is required';
    else if (exVal < -1 || exVal > 1) errors.ex = 'ex must be a number between -1 and 1';

    if (eyVal === null) errors.ey = 'ey is required';
    else if (eyVal < -1 || eyVal > 1) errors.ey = 'ey must be a number between -1 and 1';

    if (exVal !== null && eyVal !== null) {
      const eMag = Math.hypot(exVal, eyVal);
      if (eMag >= 1.0) {
        errors.ex = 'Eccentricity magnitude (sqrt(ex^2 + ey^2)) must be less than 1';
      }
    }

    const hxVal = parseFiniteNumber(s.hx);
    const hyVal = parseFiniteNumber(s.hy);
    if (hxVal === null) errors.hx = 'hx is required';
    if (hyVal === null) errors.hy = 'hy is required';

    requireAngle('longitude', 'Longitude');

    if (!errors.sma && !errors.ex && !errors.ey && !errors.hx && !errors.hy && !errors.longitude) {
      const kep = getKeplerianFromEquinoctial(s);
      if (!kep) {
        errors.sma = 'Equinoctial combination results in an invalid orbit';
      } else {
        const rp = kep.semiMajorAxis * (1 - kep.eccentricity);
        const periapsisAltKm = rp - earthRadiusKm;
        if (periapsisAltKm < ORBIT_LIMITS.altitudeKm.min) {
          errors.sma = `Orbit intersects the Earth (periapsis altitude ${periapsisAltKm.toFixed(1)} km is less than ${ORBIT_LIMITS.altitudeKm.min} km)`;
        }
      }
    }
  } else if (s.orbitType === 'inclined') {
    if (s.smaMode === 'sma') {
      requireSma();
    } else {
      requireAltitude(s.altitude, 'altitude', 'Altitude');
    }
    const e = parseFiniteNumber(s.eccentricity);
    if (e === null) {
      errors.eccentricity = 'Eccentricity is required';
    } else {
      const R_Earth = 6378;
      const h_perigee_min = 150;
      const altKm = s.smaMode === 'sma'
        ? (s.smaUnit === 'm' ? (parseFloat(s.sma) || 0) / 1000 : (parseFloat(s.sma) || 0)) - R_Earth
        : altToKm(s.altitude || '0', s.altUnit);
      const a = R_Earth + altKm;
      const r_p_min = R_Earth + h_perigee_min;
      const e_max = (a - r_p_min) / a;

      if (e < 0) {
        errors.eccentricity = 'Eccentricity cannot be negative';
      } else if (e >= 1) {
        errors.eccentricity = 'Eccentricity must be < 1 for a closed orbit (e≥1 is parabolic/hyperbolic)';
      } else if (e > e_max) {
        errors.eccentricity = `Max allowed eccentricity for ${parseFloat(altKm.toFixed(6))} km altitude is ${parseFloat(e_max.toFixed(6))}`;
      }
    }
    requireAngle('inclination', 'Inclination', ORBIT_LIMITS.inclinationDeg.min, ORBIT_LIMITS.inclinationDeg.max);
    requireAngle('raan', 'RAAN');
    requireAngle('aop', 'AOP');
    requireAngle('trueAnomaly', 'TA');
  } else if (s.orbitType === 'sso') {
    if (s.smaMode === 'sma') {
      requireSma();
    } else {
      requireAltitude(s.altitude, 'altitude', 'Altitude');
      const altKm = altToKm(s.altitude, s.altUnit);
      if (!isNaN(altKm)) {
        if (altKm > 5974) {
          errors.altitude = `No physically valid SSO inclination exists for altitude ${altKm} km (max 5974 km)`;
        }
      }
    }
    requireAngle('inclination', 'Inclination', ORBIT_LIMITS.inclinationDeg.min, ORBIT_LIMITS.inclinationDeg.max);
    if (!isValidLtan(s.ltan)) errors.ltan = 'LTAN must be in HH:MM:SS format';
  } else if (s.orbitType === 'geo') {
    requireAngle('longitude', 'Longitude', ORBIT_LIMITS.longitudeDeg.min, ORBIT_LIMITS.longitudeDeg.max);
  } else if (s.orbitType === 'heo') {
    const perigee = altToKm(s.perigee, s.altUnit);
    const apogee = altToKm(s.apogee, s.altUnit);
    requireAltitude(s.perigee, 'perigee', 'Perigee');
    if (parseFiniteNumber(s.apogee) === null) errors.apogee = 'Apogee altitude is required';
    else if (!inRange(apogee, ORBIT_LIMITS.altitudeKm.min, ORBIT_LIMITS.altitudeKm.max)) {
      const isMeter = s.altUnit === 'm';
      const minAlt = isMeter ? ORBIT_LIMITS.altitudeKm.min * 1000 : ORBIT_LIMITS.altitudeKm.min;
      const maxAlt = isMeter ? ORBIT_LIMITS.altitudeKm.max * 1000 : ORBIT_LIMITS.altitudeKm.max;
      errors.apogee = `Apogee altitude must be a number between ${minAlt} and ${maxAlt} ${s.altUnit}`;
    }
    else if (apogee <= perigee) errors.apogee = 'Apogee must be greater than perigee';
    requireAngle('inclination', 'Inclination', ORBIT_LIMITS.inclinationDeg.min, ORBIT_LIMITS.inclinationDeg.max);
    requireAngle('raan', 'RAAN');
  }

  if (Object.keys(errors).length === 0) {
    const kep = orbitStateToKeplerian(s);
    if (!kep) {
      errors.orbit = 'Orbit values are not physically valid';
    } else {
      const rp = kep.semiMajorAxis * (1 - kep.eccentricity);
      const periapsisAltKm = rp - earthRadiusKm;
      if (periapsisAltKm < ORBIT_LIMITS.altitudeKm.min) {
        const errMsg = "Invalid orbital configuration. The orbit intersects the Earth's surface. Please modify the orbital parameters.";
        if (s.inputType === 'cartesian') {
          errors.position = errMsg;
        } else if (s.inputType === 'equinoctial') {
          errors.sma = errMsg;
        } else if (s.orbitType === 'heo') {
          errors.perigee = errMsg;
        } else if (s.smaMode === 'sma') {
          errors.sma = errMsg;
        } else {
          errors.altitude = errMsg;
        }
      }
    }
  }
  return errors;
}

function validateName(name: string, label: string, satellites: any[], excludeId?: string | null): ValidationErrors {
  const trimmed = name.trim();
  if (!trimmed) return { name: `${label} is required` };
  if (trimmed.length > 64) return { name: `${label} must be 64 characters or fewer` };
  const exists = satellites.some(s => {
    if (s.id === excludeId) return false;
    if (s.type === 'group') {
      return s.children?.some((c: any) => c.id !== excludeId && c.name.trim().toLowerCase() === trimmed.toLowerCase()) ||
        s.name.trim().toLowerCase() === trimmed.toLowerCase();
    }
    return s.name.trim().toLowerCase() === trimmed.toLowerCase();
  });
  if (exists) {
    return { name: `${label} already exists` };
  }
  return {};
}

function validateGroupConfig(cfg: GroupOrbitConfig): ValidationErrors {
  const errors: ValidationErrors = {};
  const toDeg = (value: string) =>
    cfg.groupAngleUnit === 'rad' ? (parseFiniteNumber(value) ?? NaN) * (180 / Math.PI) : parseFiniteNumber(value);
  if (!isIntegerInRange(cfg.numSats, ORBIT_LIMITS.groupSatellites.min, ORBIT_LIMITS.groupSatellites.max)) {
    errors.numSats = `Number of Satellites must be a valid integer between ${ORBIT_LIMITS.groupSatellites.min} and ${ORBIT_LIMITS.groupSatellites.max}`;
  }
  if (cfg.groupType === 'multi') {
    if (!isIntegerInRange(cfg.numPlanes, ORBIT_LIMITS.groupPlanes.min, ORBIT_LIMITS.groupPlanes.max)) {
      errors.numPlanes = `Number of Planes must be a valid integer between ${ORBIT_LIMITS.groupPlanes.min} and ${ORBIT_LIMITS.groupPlanes.max}`;
    }
    if (!cfg.evenSpacing) {
      const planeSepDeg = toDeg(cfg.planeSepAngle);
      const maxVal = cfg.orbitType === 'sso' ? 180 : ORBIT_LIMITS.groupAngleDeg.max;
      if (planeSepDeg === null || !Number.isFinite(planeSepDeg)) errors.planeSepAngle = 'Plane Separation Angle is required';
      else if (!inRange(planeSepDeg, ORBIT_LIMITS.groupAngleDeg.min, maxVal)) {
        errors.planeSepAngle = `Plane Separation Angle must be a number between ${ORBIT_LIMITS.groupAngleDeg.min} and ${maxVal}°`;
      }
    }
  }
  if (!cfg.evenInterPhasing) {
    const phasingDeg = toDeg(cfg.interPlanePhasing);
    const maxVal = cfg.orbitType === 'sso' ? 180 : ORBIT_LIMITS.groupAngleDeg.max;
    if (phasingDeg === null || !Number.isFinite(phasingDeg)) errors.interPlanePhasing = 'Inter-plane Phasing is required';
    else if (!inRange(phasingDeg, ORBIT_LIMITS.groupAngleDeg.min, maxVal)) {
      errors.interPlanePhasing = `Inter-plane Phasing must be a number between ${ORBIT_LIMITS.groupAngleDeg.min} and ${maxVal}°`;
    }
  }
  return errors;
}

// ── Main component ─────────────────────────────────────────────────────────────

const AddSatModal: React.FC = () => {
  const {
    addSatModalOpen,
    closeAddSat,
    addSatellite,
    satellites,
    setSatellites,
    leftPanelCollapsed,
    modelData,
    groupEditTargetId,
    orbitalEditTargetId,
    updateSatellite,
    updateSatellites,
    invalidateAccessWindows,
    resetOrbitData,
    selectTreeItem,
  } = useMDCockpit();
  const dragStyle = {
    position: 'absolute' as const,
    top: 60,
    left: leftPanelCollapsed ? 12 : 236,
  };
  const cesium = useCesium();
  const { mission_id, scenario_id } = useParams<{ mission_id: string; scenario_id: string }>();
  const [activeTab, setActiveTab] = useState<TabId>('satellite');
  const [minimized, setMinimized] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formErrors, setFormErrors] = useState<ValidationErrors>({});
  const clearFormError = useCallback((key: string) => {
    setFormErrors(prev => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  // Satellite tab
  const [satName, setSatName] = useState('Satellite_01');
  const [orbit, setOrbit] = useState(defaultOrbit());
  const patchOrbit = useCallback(
    (patch: Partial<OrbitState>) => setOrbit(o => ({ ...o, ...patch })),
    []
  );

  // Group tab
  const [groupName, setGroupName] = useState('Group_01');
  const [refOrbit, setRefOrbit] = useState(defaultOrbit());
  const patchRefOrbit = useCallback(
    (patch: Partial<OrbitState>) => setRefOrbit(o => ({ ...o, ...patch })),
    []
  );
  const [groupType, setGroupType] = useState<GroupType>('single');
  const [numSats, setNumSats] = useState('3');
  const [numPlanes, setNumPlanes] = useState('3');
  const [planeSepAngle, setPlaneSepAngle] = useState('60');
  const [evenSpacing, setEvenSpacing] = useState(true);
  const [interPlanePhasing, setInterPlanePhasing] = useState('120');
  const [evenInterPhasing, setEvenInterPhasing] = useState(true);
  const [groupAngleUnit, setGroupAngleUnit] = useState<'deg' | 'rad'>('deg');
  const groupEditTarget = useMemo(
    () => groupEditTargetId
      ? satellites.find((item): item is SatGroup => (item as SatGroup).type === 'group' && item.id === groupEditTargetId) ?? null
      : null,
    [groupEditTargetId, satellites],
  );
  const groupEditMode = !!groupEditTargetId;

  const satEditTarget = useMemo(() => {
    if (!orbitalEditTargetId) return null;
    for (const item of satellites) {
      if ((item as SatGroup).type === 'group') {
        const child = (item as SatGroup).children.find(c => c.id === orbitalEditTargetId);
        if (child) return child;
      } else if ((item as Satellite).id === orbitalEditTargetId) {
        return item as Satellite;
      }
    }
    return null;
  }, [orbitalEditTargetId, satellites]);
  const satEditMode = !!satEditTarget;

  const [groupStep, setGroupStep] = useState<1 | 2>(1);
  const [refOrbitOpen, setRefOrbitOpen] = useState(true);
  const [groupConfigOpen, setGroupConfigOpen] = useState(false);

  useEffect(() => {
    if (groupStep === 1) {
      setRefOrbitOpen(true);
      setGroupConfigOpen(false);
    } else {
      setRefOrbitOpen(false);
      setGroupConfigOpen(true);
    }
  }, [groupStep]);

  // TLE tab
  const [tleName, setTleName] = useState('');
  const [tleLine1, setTleLine1] = useState('');
  const [tleLine2, setTleLine2] = useState('');
  const [tleTextPaste, setTleTextPaste] = useState('');
  const [tleEntries, setTleEntries] = useState<TleEntry[]>([]);
  const [tlePreviewLoading, setTlePreviewLoading] = useState(false);
  const [tleError, setTleError] = useState('');
  const [isTleFileUploaded, setIsTleFileUploaded] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const tleFileRef = useRef<HTMLInputElement>(null);

  // Reset ALL form state to defaults every time the modal opens
  useEffect(() => {
    if (!addSatModalOpen) return;
    setMinimized(false);
    cesium.setClockPlaying(false);
    const satCount = satellites.length + 1;

    let satEdit: Satellite | null = null;
    if (orbitalEditTargetId) {
      for (const item of satellites) {
        if ((item as SatGroup).type === 'group') {
          const child = (item as SatGroup).children.find(c => c.id === orbitalEditTargetId);
          if (child) { satEdit = child; break; }
        } else if ((item as Satellite).id === orbitalEditTargetId) {
          satEdit = item as Satellite;
          break;
        }
      }
    }

    const isTleGroup = !!(groupEditTarget?.children?.[0]?.tleMeta);
    const initialTleName = satEdit?.name ?? groupEditTarget?.name ?? '';
    const initialTleL1 = satEdit?.tleMeta?.line1 ?? groupEditTarget?.children?.[0]?.tleMeta?.line1 ?? '';
    const initialTleL2 = satEdit?.tleMeta?.line2 ?? groupEditTarget?.children?.[0]?.tleMeta?.line2 ?? '';

    if (isTleGroup) {
      setActiveTab('tle');
    } else if (groupEditTarget) {
      setActiveTab('group');
    } else if (satEdit) {
      setActiveTab(satEdit.tleMeta ? 'tle' : 'satellite');
    } else {
      setActiveTab('satellite');
    }

    const defaultEpoch = modelData?.propagation?.startEpoch
      ? epochToDisplay(modelData.propagation.startEpoch)
      : getUtcNowDisplay();

    const gProps = groupEditTarget?.groupProperties;
    const gType = gProps?.type === 'Multi Plane' ? 'multi' : 'single';

    const initialNumSats = groupEditTarget
      ? (gProps?.type === 'Multi Plane'
        ? (gProps.noOfSatellitesPerPlane ?? gProps.noOfSatellites ?? groupEditTarget.children?.length ?? 1)
        : (gProps?.noOfSatellites ?? groupEditTarget.children?.length ?? 1))
      : 3;

    const initialNumPlanes = groupEditTarget && gProps?.type === 'Multi Plane'
      ? (gProps.noOfPlanes ?? 3)
      : 3;

    const initialPlaneSep = groupEditTarget && gProps?.type === 'Multi Plane'
      ? (gProps.separationAngleBetweenPlane ?? 60)
      : 60;

    const initialPhasing = groupEditTarget
      ? (gProps?.type === 'Multi Plane'
        ? gProps.interAnglePhasingForWalkerDelta
        : gProps?.interAnglePhasingForSinglePlane)
      : undefined;

    const editRefOrbit = groupEditTarget ? orbitStateFromGroup(groupEditTarget) : defaultOrbit(defaultEpoch);
    const baseAngle = editRefOrbit.orbitType === 'sso' ? 180 : 360;

    const expectedEvenPhasing = Math.round((baseAngle / Math.max(1, Number(initialNumSats))) * 100) / 100;
    const isEvenPhasing = groupEditTarget
      ? (initialPhasing == null || Math.abs(Number(initialPhasing) - expectedEvenPhasing) < 0.05)
      : true;

    const expectedEvenPlaneSep = Math.round((baseAngle / Math.max(1, Number(initialNumPlanes))) * 100) / 100;
    const isEvenPlaneSep = groupEditTarget
      ? (initialPlaneSep == null || Math.abs(Number(initialPlaneSep) - expectedEvenPlaneSep) < 0.05)
      : true;

    setSatName(satEdit?.name ?? `Satellite_${String(satCount).padStart(2, '0')}`);
    setOrbit(satEdit ? orbitStateFromSatellite(satEdit) : defaultOrbit(defaultEpoch));
    setGroupName(groupEditTarget?.name ?? `Group_${String(satCount).padStart(2, '0')}`);
    setRefOrbit(editRefOrbit);
    setGroupType(groupEditTarget ? gType : 'single');
    setNumSats(String(initialNumSats));
    setNumPlanes(String(initialNumPlanes));
    setPlaneSepAngle(String(initialPlaneSep));
    setEvenSpacing(isEvenPlaneSep);
    setInterPlanePhasing(groupEditTarget && initialPhasing != null ? String(initialPhasing) : String(expectedEvenPhasing));
    setEvenInterPhasing(isEvenPhasing);
    setGroupAngleUnit('deg');
    setTleName(initialTleName);
    setTleLine1(initialTleL1);
    setTleLine2(initialTleL2);
    setTleTextPaste(initialTleL1 && initialTleL2 ? `${initialTleName}\n${initialTleL1}\n${initialTleL2}` : '');
    setTleEntries([]);
    setTlePreviewLoading(false);
    setTleError('');
    setIsTleFileUploaded(false);
    setUploadedFileName('');
    setCreating(false);
    setFormErrors({});
    setGroupStep(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addSatModalOpen, groupEditTargetId, orbitalEditTargetId]);

  useEffect(() => {
    setFormErrors({});
    setGroupStep(1);
    setOrbit(prev => {
      let changed = false;
      const next = { ...prev };
      if (!next.eccentricity || !next.eccentricity.trim()) { next.eccentricity = '0'; changed = true; }
      if (!next.raan || !next.raan.trim()) { next.raan = '0'; changed = true; }
      if (!next.aop || !next.aop.trim()) { next.aop = '0'; changed = true; }
      if (!next.trueAnomaly || !next.trueAnomaly.trim()) { next.trueAnomaly = '0'; changed = true; }
      return changed ? next : prev;
    });
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'group') return;
    setRefOrbit(prev => {
      const orbitType = prev.orbitType === 'geo' || prev.orbitType === 'heo'
        ? 'inclined'
        : prev.orbitType;
      if (prev.inputType === 'keplerian' && prev.orbitType === orbitType) return prev;
      // Reset to Keplerian if switching away from inclined and currently using Cartesian/Equinoctial
      const shouldResetInputType = orbitType !== 'inclined' && (prev.inputType === 'cartesian' || prev.inputType === 'equinoctial');
      return { ...prev, inputType: shouldResetInputType ? 'keplerian' : prev.inputType, orbitType };
    });
  }, [activeTab]);

  const validateAndSetTle = (l1: string, l2: string, keepBulk = false) => {
    const norm = normalizeTlePair(l1, l2);
    if (norm) {
      setTleLine1(norm.line1);
      setTleLine2(norm.line2);
      setTleError(validateTlePair(norm.line1, norm.line2));
    } else {
      setTleLine1(l1);
      setTleLine2(l2);
      setTleError(validateTlePair(l1, l2));
    }
    if (!keepBulk) setTleEntries([]);
  };

  const applyTleFromText = (text: string, suppressError = false) => {
    const bulk = parseTleFileContent(text);
    if (bulk.length > 1) {
      setTleEntries(bulk);
      const first = bulk[0];
      if (first.name) setTleName(first.name);
      validateAndSetTle(first.line1, first.line2, true);
      return;
    }
    const parsed = parseTleText(text);
    if (!parsed) {
      if (!suppressError) {
        setTleError('Could not parse TLE — paste 2 lines (1 … and 2 …) or 3-line with name');
      }
      return;
    }
    setTleEntries([]);
    if (parsed.name) setTleName(parsed.name);
    validateAndSetTle(parsed.line1, parsed.line2);
  };

  const handleTleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.txt')) {
      showToastMessage('info', 'Please upload a .txt file');
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? '');
      const entries = parseTleFileContent(text);
      if (!entries.length) {
        showToastMessage('info', 'No valid TLE pairs found in file');
        setTleError('No valid TLE pairs found in file');
        return;
      }
      setTleEntries(entries);
      const first = entries[0];
      setTleName(first.name);
      validateAndSetTle(first.line1, first.line2, true);
      setTleTextPaste(text);
      setTleError('');
      setIsTleFileUploaded(true);
      setUploadedFileName(file.name);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleClearTleFile = () => {
    setTleTextPaste('');
    setTleLine1('');
    setTleLine2('');
    setTleName('');
    setTleEntries([]);
    setTleError('');
    setUploadedFileName('');
    setIsTleFileUploaded(false);
  };

  const handleTleTextPaste = (text: string) => {
    clearFormError('tle');
    setTleError('');
    setTleTextPaste(text);
    applyTleFromText(text);
  };

  const handleTleTextChange = (val: string) => {
    clearFormError('tle');
    setTleError('');
    setTleTextPaste(val);

    // Sync Line 1 and Line 2 inputs
    const parsed = parseTleText(val);
    if (parsed) {
      setTleLine1(parsed.line1);
      setTleLine2(parsed.line2);
    }

    if (val.trim()) {
      applyTleFromText(val, true);
    }
  };

  const handleTleTextBlur = (val: string) => {
    if (val.trim()) applyTleFromText(val, false);
  };

  const handleGroupAngleToggle = () => {
    const r = toggleAngle(planeSepAngle, groupAngleUnit);
    const r2 = toggleAngle(interPlanePhasing, groupAngleUnit);
    setPlaneSepAngle(r.value);
    setInterPlanePhasing(r2.value);
    setGroupAngleUnit(r.unit);
  };

  const groupAngleLabel = groupAngleUnit === 'deg' ? '°' : 'rad';

  const groupOrbitConfig: GroupOrbitConfig = useMemo(() => ({
    groupType,
    numSats,
    numPlanes,
    planeSepAngle,
    interPlanePhasing,
    groupAngleUnit,
    evenSpacing,
    evenInterPhasing,
    orbitType: refOrbit.orbitType,
  }), [
    groupType,
    numSats,
    numPlanes,
    planeSepAngle,
    interPlanePhasing,
    groupAngleUnit,
    evenSpacing,
    evenInterPhasing,
    refOrbit.orbitType,
  ]);

  // Auto plane separation when even-spacing is enabled (multi-plane)
  useEffect(() => {
    if (!evenSpacing || groupType !== 'multi') return;
    const n = Math.max(1, parseInt(numPlanes, 10) || 1);
    const baseAngle = refOrbit.orbitType === 'sso' ? 180 : 360;
    const sep = Math.round((baseAngle / n) * 100) / 100;
    setPlaneSepAngle(String(sep));
  }, [evenSpacing, numPlanes, groupType, refOrbit.orbitType]);

  // Auto inter-plane phasing when evenInterPhasing is enabled
  useEffect(() => {
    if (!evenInterPhasing) return;
    const n = Math.max(1, parseInt(numSats, 10) || 1);
    const baseAngle = refOrbit.orbitType === 'sso' ? 180 : 360;
    const phasing = Math.round((baseAngle / n) * 100) / 100;
    setInterPlanePhasing(String(phasing));
  }, [evenInterPhasing, numSats, refOrbit.orbitType]);

  // Clear preview when modal closes
  useEffect(() => {
    if (!addSatModalOpen) cesium.clearOrbitPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addSatModalOpen]);

  // Immediate preview when modal opens or tab changes (no debounce)
  useEffect(() => {
    if (!addSatModalOpen) return;

    if (activeTab === 'satellite') {
      const isValidForPreview = Object.keys(validateOrbitState(orbit, modelData)).length === 0;
      const params = isValidForPreview ? orbitStateToKeplerian(orbit) : null;
      if (params) {
        cesium.showOrbitPreview([params]);
      } else {
        cesium.clearOrbitPreview();
      }
    } else if (activeTab === 'group') {
      const isValidForPreview = Object.keys(validateOrbitState(refOrbit, modelData)).length === 0;
      const base = isValidForPreview ? orbitStateToKeplerian(refOrbit) : null;
      if (base) {
        cesium.showOrbitPreview(buildGroupOrbits(base, groupOrbitConfig));
      } else {
        cesium.clearOrbitPreview();
      }
    } else if (activeTab === 'tle') {
      cesium.clearOrbitPreview();
    }
  }, [
    addSatModalOpen, activeTab, orbit, refOrbit, numSats, numPlanes, groupType,
    planeSepAngle, interPlanePhasing, groupAngleUnit, evenSpacing, groupOrbitConfig, modelData,
  ]);

  // TLE map preview: SGP4 on manual lines
  useEffect(() => {
    if (!addSatModalOpen || activeTab !== 'tle') return;

    const norm = normalizeTlePair(tleLine1, tleLine2);
    const tleComplete = !!norm && !validateTlePair(norm.line1, norm.line2);

    if (!tleComplete) {
      cesium.clearOrbitPreview();
      return;
    }

    let cancelled = false;
    setTlePreviewLoading(true);

    (async () => {
      try {
        const toPreview = tleEntries.length > 0
          ? tleEntries
          : [{ name: tleName, line1: tleLine1, line2: tleLine2 }];

        const resolved = await Promise.all(
          toPreview.map(e => resolveTleForPreview(e.line1, e.line2, e.name || tleName)),
        );

        if (cancelled) return;
        cesium.showTleOrbitPreview(resolved.map(r => ({ line1: r.line1, line2: r.line2 })));
      } catch (err) {
        console.error('[TLE] Preview failed', err);
        if (!cancelled) {
          setTleError('Failed to fetch TLE');
          cesium.clearOrbitPreview();
        }
      } finally {
        if (!cancelled) setTlePreviewLoading(false);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addSatModalOpen, activeTab, tleLine1, tleLine2, tleName, tleEntries]);

  if (!addSatModalOpen) return null;

  // Build a typed OrbitMeta from orbit form state
  const buildOrbitMeta = (s: OrbitState): OrbitMeta => {
    const params = orbitStateToKeplerian(s);
    const earthRadiusKm = getEarthRadiusKm(s.altDatum);
    const LABEL: Record<string, string> = {
      inclined: 'Inclined',
      sso: 'Sun-Synchronous',
      geo: 'Geostationary',
      heo: 'Highly Elliptical',
    };
    const type = String(s.orbitType);   // ensure runtime string (not type-erased)
    const base: OrbitMeta = {
      orbitType: type as OrbitType,
      orbitTypeLabel: LABEL[type] ?? type,
      epoch: s.epoch,
      refFrame: s.inputType === 'cartesian' ? s.cartRefFrame : s.refFrame,
      altitude_km: params ? (params.eccentricity > 0 ? params.semiMajorAxis * (1 - params.eccentricity) - earthRadiusKm : params.semiMajorAxis - earthRadiusKm) : parseFloat(s.altitude) || 0,
      eccentricity: params ? params.eccentricity : parseFloat(s.eccentricity) || 0,
      inclination_deg: params ? params.inclination : parseFloat(s.inclination) || 0,
      raan_deg: params ? params.RAAN : parseFloat(s.raan) || 0,
      aop_deg: params ? params.AOP : parseFloat(s.aop) || 0,
      trueAnomaly_deg: params ? params.TA : parseFloat(s.trueAnomaly) || 0,
    };
    if (type === 'sso') return { ...base, ltan: s.ltan };
    if (type === 'geo') return { ...base, longitude_deg: parseFloat(s.longitude) || 0 };
    if (type === 'heo') return { ...base, perigee_km: parseFloat(s.perigee) || 0, apogee_km: parseFloat(s.apogee) || 0 };
    return base;
  };

  const refreshSatellitesFromBackend = async (excludeSatelliteIds?: string[]): Promise<boolean> => {
    if (!mission_id || !scenario_id) return false;
    const [groupRes, orbitRes] = await Promise.allSettled([
      getSatelliteGroups(mission_id, scenario_id),
      getAllSatelliteGroupOrbit(mission_id, scenario_id),
    ]);
    if (groupRes.status !== 'fulfilled') {
      console.error('[SatelliteGroups] Refresh after create failed', groupRes.reason);
      return false;
    }

    const list: any[] = groupRes.value?.data?.data ?? groupRes.value?.data ?? [];
    if (!Array.isArray(list)) return false;

    const reports: any[] = orbitRes.status === 'fulfilled'
      ? orbitRes.value?.data?.data ?? orbitRes.value?.data ?? []
      : [];
    if (orbitRes.status === 'rejected') {
      console.error('[OrbitPoints] Refresh after create failed', orbitRes.reason);
    }

    const mapped = list.map(mapApiSatToTreeItem);
    const filteredMapped = excludeSatelliteIds && excludeSatelliteIds.length > 0
      ? mapped.map(item => {
        if ((item as SatGroup).type === 'group') {
          const grp = item as SatGroup;
          return {
            ...grp,
            children: grp.children.map(child => {
              if (excludeSatelliteIds.includes(child.id)) {
                return {
                  ...child,
                  backendOrbitTrack: undefined,
                  ephemeris: undefined,
                  propagationStatus: 'preview' as const,
                  orbitPointStatus: undefined,
                };
              }
              return child;
            }),
          };
        }
        const sat = item as Satellite;
        if (excludeSatelliteIds.includes(sat.id)) {
          return {
            ...sat,
            backendOrbitTrack: undefined,
            ephemeris: undefined,
            propagationStatus: 'preview' as const,
            orbitPointStatus: undefined,
          };
        }
        return item;
      })
      : mapped;

    setSatellites(Array.isArray(reports) && reports.length > 0
      ? applyOrbitReportUpdatesToTreeItems(filteredMapped, reports)
      : filteredMapped);
    return true;
  };

  const handleNextGroupStep = () => {
    const orbitalErrors = validateOrbitState(refOrbit, modelData);
    if (Object.keys(orbitalErrors).length > 0) {
      setFormErrors(orbitalErrors);
      const errMsgs = Object.values(orbitalErrors).filter(Boolean);
      if (errMsgs.length > 0) {
        showToastMessage('error', errMsgs.join('. '));
      }
      return;
    }
    setFormErrors({});
    setGroupStep(2);
  };

  const handleCreate = async () => {
    if (creating) return;
    if (!mission_id || !scenario_id) {
      console.error('[AddSatModal] mission_id or scenario_id missing');
      return;
    }
    if (activeTab === 'satellite') {
      const validationErrors: ValidationErrors = {
        ...validateName(satName, 'Satellite name', satellites, satEditTarget?.id),
        ...validateOrbitState(orbit, modelData),
      };
      if (Object.keys(validationErrors).length > 0) {
        setFormErrors(validationErrors);
        const errMsgs = Object.values(validationErrors).filter(Boolean);
        if (errMsgs.length > 0) {
          showToastMessage('error', errMsgs.join('. '));
        }
        return;
      }
      const params = orbitStateToKeplerian(orbit);
      if (!params) return;
      const orbitMeta = buildOrbitMeta(orbit);
      const id = `sat-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

      // ── API request body ──
      const suffix = orbit.epochTz === 'IST' ? '+05:30' : 'Z';
      const epochSec = new Date(orbit.epoch.replace(' ', 'T') + suffix).getTime() / 1000;
      const altKm = altToKm(orbit.altitude, orbit.altUnit);
      const ecc = parseFloat(orbit.eccentricity) || 0;
      const earthRadiusKm = getEarthRadiusKm(orbit.altDatum);
      const smaKm = orbit.smaMode === 'sma' ? (parseFloat(orbit.sma) || 0) : (ecc > 0 ? (altKm + earthRadiusKm) / (1 - ecc) : altKm + earthRadiusKm);
      const altKmFinal = (ecc > 0 ? smaKm * (1 - ecc) : smaKm) - earthRadiusKm;
      const toReqDeg = (v: string) => angleConvert(v, orbit.angleUnit);
      const orbitTypeLabel = orbit.orbitType === 'inclined' ? 'Inclined' : orbit.orbitType.toUpperCase();

      // Determine orbital elements type and build reference orbit based on input type
      let orbitalElementsType: string;
      let referenceOrbit: Record<string, unknown>;

      if (orbit.inputType === 'keplerian') {
        orbitalElementsType = 'Keplarian';
        referenceOrbit = { epoch: epochSec, altitudeReference: orbit.altDatum || 'MSL' };
        if (orbit.orbitType === 'inclined') {
          Object.assign(referenceOrbit, {
            semiMajorAxis: smaKm,
            eccentricity: parseFloat(orbit.eccentricity) || 0,
            inclination: sanitizeInclination(toReqDeg(orbit.inclination)),
            RAAN: normalizeAngle(toReqDeg(orbit.raan)),
            AOP: normalizeAngle(toReqDeg(orbit.aop)),
            TA: normalizeAngle(toReqDeg(orbit.trueAnomaly)),
            altitude: altKmFinal
          });
        } else if (orbit.orbitType === 'sso') {
          Object.assign(referenceOrbit, {
            semiMajorAxis: smaKm,
            LTAN: orbit.ltan,
            eccentricity: 0,
            inclination: sanitizeInclination(toReqDeg(orbit.inclination)),
            RAAN: normalizeAngle(toReqDeg(orbit.raan)),
            altitude: altKmFinal,
            TA: 0
          });
        } else if (orbit.orbitType === 'geo') {
          Object.assign(referenceOrbit, { longitude: toReqDeg(orbit.longitude), semiMajorAxis: GEO_SMA_KM, eccentricity: 0, inclination: 0, altitude: 35786 });
        } else if (orbit.orbitType === 'heo') {
          const perigeeKm = altToKm(orbit.perigee, orbit.altUnit);
          const apogeeKm = altToKm(orbit.apogee, orbit.altUnit);
          Object.assign(referenceOrbit, {
            perigeeDistance: perigeeKm,
            apogeeDistance: apogeeKm,
            inclination: sanitizeInclination(toReqDeg(orbit.inclination)),
            RAAN: normalizeAngle(toReqDeg(orbit.raan)),
            AOP: normalizeAngle(toReqDeg(orbit.aop))
          });
        }
      } else if (orbit.inputType === 'cartesian') {
        orbitalElementsType = 'Cartesian';
        const posXKm = altToKm(orbit.posX, orbit.posUnit);
        const posYKm = altToKm(orbit.posY, orbit.posUnit);
        const posZKm = altToKm(orbit.posZ, orbit.posUnit);
        const velXKmS = orbit.velUnit === 'm/s' ? parseFloat(orbit.velX) / 1000 : parseFloat(orbit.velX);
        const velYKmS = orbit.velUnit === 'm/s' ? parseFloat(orbit.velY) / 1000 : parseFloat(orbit.velY);
        const velZKmS = orbit.velUnit === 'm/s' ? parseFloat(orbit.velZ) / 1000 : parseFloat(orbit.velZ);
        referenceOrbit = {
          epoch: epochSec,
          position: [posXKm, posYKm, posZKm],
          velocity: [velXKmS, velYKmS, velZKmS],
          altitudeReference: orbit.altDatum || 'MSL',
        };
      } else if (orbit.inputType === 'equinoctial') {
        orbitalElementsType = 'Equinoctial';
        const smaKm = orbit.smaUnit === 'm' ? parseFloat(orbit.sma) / 1000 : parseFloat(orbit.sma);
        const exVal = parseFloat(orbit.ex) || 0;
        const eyVal = parseFloat(orbit.ey) || 0;
        const hxVal = parseFloat(orbit.hx) || 0;
        const hyVal = parseFloat(orbit.hy) || 0;
        referenceOrbit = {
          epoch: epochSec,
          sma: smaKm,
          eccentricityVector: [exVal, eyVal],
          inclinationVector: [hxVal, hyVal],
          longitude: toReqDeg(orbit.longitude),
          altitudeReference: orbit.altDatum || 'MSL',
        };
      } else {
        orbitalElementsType = 'Keplarian';
        referenceOrbit = { epoch: epochSec, altitudeReference: orbit.altDatum || 'MSL' };
      }

      const reqBody: Record<string, unknown> = {
        name: satName || 'Satellite_01',
        defaultNaming: false,
        type: 'Single',
        referenceFrame: orbit.inputType === 'cartesian' ? orbit.cartRefFrame : orbit.refFrame,
        orbitType: orbitTypeLabel,
        orbitalElements: orbitalElementsType,
        referenceOrbit,
      };
      try {
        setCreating(true);
        if (satEditMode && satEditTarget) {
          const grpId = satEditTarget.simulationGroupId;
          resetOrbitData([satEditTarget.id], grpId ? [grpId] : []);
          cesium.setSelectedSatellite(null);
          cesium.highlightSatellite(null);

          // Update local state instantly with new parameters and propagation status 'preview'
          updateSatellite(satEditTarget.id, {
            name: satName,
            orbitMeta,
            orbitElements: params,
            propagationStatus: 'preview',
            backendOrbitTrack: undefined,
            ephemeris: undefined,
          });

          // Close modal and clear preview instantly
          cesium.clearOrbitPreview();
          closeAddSat();
          selectTreeItem(null, null);

          const isNameOnly = !!(
            (Number(satEditTarget.orbitMeta?.altitude_km) === Number(orbit.altitude)) &&
            (Number(satEditTarget.orbitMeta?.inclination_deg) === Number(orbit.inclination)) &&
            (Number(satEditTarget.orbitMeta?.raan_deg) === Number(orbit.raan)) &&
            (Number(satEditTarget.orbitMeta?.aop_deg) === Number(orbit.aop)) &&
            (Number(satEditTarget.orbitMeta?.trueAnomaly_deg) === Number(orbit.trueAnomaly)) &&
            (satEditTarget.orbitMeta?.orbitType === orbit.orbitType) &&
            ((satEditTarget.orbitMeta as any)?.inputType === orbit.inputType)
          );

          invalidateAccessWindows();

          // Call API in the background
          if (satEditTarget.simulationGroupId) {
            editSatelliteGroup(mission_id, scenario_id, satEditTarget.simulationGroupId, reqBody)
              .then((res: any) => {
                const successMsg = res?.data?.message || res?.message || 'Satellite updated successfully';
                showToastMessage('success', successMsg);
                refreshSatellitesFromBackend([satEditTarget.id]).catch(err => {
                  console.error('[Satellite] Background refresh failed', err);
                });
              })
              .catch(err => {
                console.error('[Satellite] Edit API failed', err);
                const errorMsg = err?.response?.data?.message || err?.response?.data?.Message || err?.data?.message || err?.message || 'Failed to update satellite on backend';
                showToastMessage('error', errorMsg);
              });
          }
          return;
        }

        // Create mode flow
        const res: any = await createSatelliteGroup(mission_id, scenario_id, reqBody);
        const created = unwrapCreateResponse(res);
        const satPayload = created?.satellites?.[0] ?? created?.satellite ?? created;
        const ephemeris = extractEphemeris(satPayload);
        addSatellite({
          id,
          name: satName || 'Satellite_01',
          dot: 'ok' as const,
          active: true,
          orbitMeta,
          orbitElements: params,
          physicalCharacteristics: DEFAULT_INTERNAL_PHYSICAL_CHARACTERISTICS,
          ephemeris,
          propagationStatus: ephemeris ? ('ready' as const) : ('preview' as const),
          effectivePerturbationForces: satPayload?.effectivePerturbationForces ?? satPayload?.perturbationForces ?? modelData?.perturbationForces ?? undefined,
        });
        invalidateAccessWindows();

        const successMsg = res?.data?.message || res?.message || 'Satellite created successfully';
        showToastMessage('success', successMsg);

        cesium.clearOrbitPreview();
        closeAddSat();
        selectTreeItem(null, null);

        refreshSatellitesFromBackend().catch(err => {
          console.error('[Satellite] Background refresh failed', err);
        });

        return;
      } catch (err: any) {
        console.error(satEditMode ? '[Satellite] Update failed' : '[Satellite] Create failed', err);
        const errorMessage = err?.response?.data?.message || err?.response?.data?.Message || err?.data?.message || err?.message || (satEditMode ? 'Failed to update satellite' : 'Failed to create satellite');
        setFormErrors({ orbit: errorMessage });
        showToastMessage('error', errorMessage);
        return;
      } finally {
        setCreating(false);
      }

    } else if (activeTab === 'group') {
      const validationErrors: ValidationErrors = {
        ...(groupEditMode ? {} : validateName(groupName, 'Group name', satellites)),
        ...validateOrbitState(refOrbit, modelData),
        ...validateGroupConfig(groupOrbitConfig),
      };
      if (refOrbit.inputType !== 'keplerian') validationErrors.orbit = 'Group reference orbit must be Keplerian';
      if (Object.keys(validationErrors).length > 0) {
        setFormErrors(validationErrors);
        const errMsgs = Object.values(validationErrors).filter(Boolean);
        if (errMsgs.length > 0) {
          showToastMessage('error', errMsgs.join('. '));
        }
        return;
      }
      const base = orbitStateToKeplerian(refOrbit);
      if (!base) return;
      const orbitMeta = buildOrbitMeta(refOrbit);
      const isMulti = groupType === 'multi';

      // For single plane: numSats = total sats
      // For multi plane:  numSats = satellites per plane  (per user spec)
      const nSatsInput = Math.max(1, parseInt(numSats, 10) || 3);
      const nPlanes = isMulti ? Math.max(1, parseInt(numPlanes, 10) || 1) : 1;
      const satsInPlane = nSatsInput;                        // always represents sats in one plane

      // Use actual UI angle values (convert rad→deg if needed)
      const toDeg = (val: string) =>
        groupAngleUnit === 'rad' ? (parseFloat(val) || 0) * (180 / Math.PI) : parseFloat(val) || 0;

      const baseAngle = refOrbit.orbitType === 'sso' ? 180 : 360;
      const planeSepDeg = isMulti
        ? (evenSpacing ? baseAngle / nPlanes : toDeg(planeSepAngle))
        : 0;
      const interPhasingDeg = toDeg(interPlanePhasing);

      const groupOrbits = base ? buildGroupOrbits(base, groupOrbitConfig) : [];
      const children: Satellite[] = groupOrbits.map((op, idx) => ({
        id: `sat-${Date.now()}-${Math.random().toString(36).slice(2, 7)}-${idx}`,
        name: `${groupName || 'Group_01'}_${String(idx + 1).padStart(2, '0')}`,
        dot: 'ok' as const,
        active: true,
        orbitMeta: { ...orbitMeta, raan_deg: op.RAAN, trueAnomaly_deg: op.TA },
        orbitElements: op,
        physicalCharacteristics: DEFAULT_INTERNAL_PHYSICAL_CHARACTERISTICS,
        propagationStatus: modelData?.propagation && modelData?.perturbationForces ? 'propagating' : 'preview',
        effectivePerturbationForces: modelData?.perturbationForces ?? undefined,
      }));

      // ── API request body ──
      const suffix = refOrbit.epochTz === 'IST' ? '+05:30' : 'Z';
      const groupEpochSec = new Date(refOrbit.epoch.replace(' ', 'T') + suffix).getTime() / 1000;
      const gAltKm = altToKm(refOrbit.altitude, refOrbit.altUnit);
      const gEcc = parseFloat(refOrbit.eccentricity) || 0;
      const gEarthRadiusKm = getEarthRadiusKm(refOrbit.altDatum);
      const gSmaKm = refOrbit.smaMode === 'sma' ? (parseFloat(refOrbit.sma) || 0) : (gEcc > 0 ? (gAltKm + gEarthRadiusKm) / (1 - gEcc) : gAltKm + gEarthRadiusKm);
      const gAltKmFinal = (gEcc > 0 ? gSmaKm * (1 - gEcc) : gSmaKm) - gEarthRadiusKm;
      const toGDeg = (v: string) => angleConvert(v, refOrbit.angleUnit);
      const gOrbitTypeLabel = refOrbit.orbitType === 'inclined' ? 'Inclined' : refOrbit.orbitType.toUpperCase();

      const groupReferenceOrbit: Record<string, unknown> = {
        epoch: groupEpochSec,
        altitudeReference: refOrbit.altDatum || 'MSL',
      };
      if (refOrbit.orbitType === 'inclined') {
        Object.assign(groupReferenceOrbit, {
          semiMajorAxis: gSmaKm,
          eccentricity: parseFloat(refOrbit.eccentricity) || 0,
          inclination: sanitizeInclination(toGDeg(refOrbit.inclination)),
          RAAN: normalizeAngle(toGDeg(refOrbit.raan)),
          AOP: normalizeAngle(toGDeg(refOrbit.aop)),
          TA: normalizeAngle(toGDeg(refOrbit.trueAnomaly)),
          altitude: gAltKmFinal
        });
      } else if (refOrbit.orbitType === 'sso') {
        Object.assign(groupReferenceOrbit, {
          semiMajorAxis: gSmaKm,
          LTAN: refOrbit.ltan,
          eccentricity: 0,
          inclination: sanitizeInclination(toGDeg(refOrbit.inclination)),
          RAAN: normalizeAngle(toGDeg(refOrbit.raan)),
          altitude: gAltKmFinal,
          TA: 0
        });
      } else if (refOrbit.orbitType === 'geo') {
        Object.assign(groupReferenceOrbit, { longitude: toGDeg(refOrbit.longitude), semiMajorAxis: GEO_SMA_KM, eccentricity: 0, inclination: 0, altitude: 35786 });
      } else if (refOrbit.orbitType === 'heo') {
        const gPerigeeKm = altToKm(refOrbit.perigee, refOrbit.altUnit);
        const gApogeeKm = altToKm(refOrbit.apogee, refOrbit.altUnit);
        Object.assign(groupReferenceOrbit, {
          perigeeDistance: gPerigeeKm,
          apogeeDistance: gApogeeKm,
          inclination: sanitizeInclination(toGDeg(refOrbit.inclination)),
          RAAN: normalizeAngle(toGDeg(refOrbit.raan)),
          AOP: normalizeAngle(toGDeg(refOrbit.aop))
        });
      }

      const groupProperties = isMulti
        ? { type: 'Multi Plane', noOfPlanes: nPlanes, noOfSatellitesPerPlane: satsInPlane, separationAngleBetweenPlane: planeSepDeg, interAnglePhasingForWalkerDelta: interPhasingDeg }
        : { type: 'Single Plane', noOfSatellites: nSatsInput, interAnglePhasingForSinglePlane: interPhasingDeg };

      const groupReqBody: Record<string, unknown> = {
        name: groupName || 'Group_01',
        defaultNaming: false,
        type: 'Group',
        referenceFrame: refOrbit.refFrame,
        orbitType: gOrbitTypeLabel,
        orbitalElements: 'Keplarian',
        groupProperties,
        referenceOrbit: groupReferenceOrbit,
      };
      try {
        setCreating(true);
        if (groupEditMode && groupEditTargetId) {
          const childIds = groupEditTarget?.children?.map(child => child.id) ?? [];
          resetOrbitData(childIds, [groupEditTargetId]);
          cesium.setSelectedSatellite(null);
          cesium.highlightSatellite(null);

          // Instantly replace group children with newly computed Keplerian preview children in state
          const updatedSatellites = satellites.map(item => {
            if (item.id === groupEditTargetId) {
              return {
                ...item,
                name: groupName || item.name,
                children: children.map(child => ({
                  ...child,
                  propagationStatus: 'preview' as const,
                  backendOrbitTrack: undefined,
                  ephemeris: undefined,
                })),
              } as SatGroup;
            }
            return item;
          });
          setSatellites(updatedSatellites);

          // Close modal and clear preview instantly
          cesium.clearOrbitPreview();
          closeAddSat();
          selectTreeItem(null, null);

          const isGroupNameOnly = !!(groupEditMode && groupEditTarget &&
            Number(groupEditTarget.children?.[0]?.orbitMeta?.altitude_km) === Number(refOrbit.altitude) &&
            Number(groupEditTarget.children?.[0]?.orbitMeta?.inclination_deg) === Number(refOrbit.inclination) &&
            Number(groupEditTarget.children?.[0]?.orbitMeta?.raan_deg) === Number(refOrbit.raan) &&
            Number(groupEditTarget.children?.[0]?.orbitMeta?.aop_deg) === Number(refOrbit.aop) &&
            Number(groupEditTarget.children?.[0]?.orbitMeta?.trueAnomaly_deg) === Number(refOrbit.trueAnomaly) &&
            groupEditTarget.children?.length === Number(numSats));

          invalidateAccessWindows();

          // Call API in background
          editSatelliteGroup(mission_id, scenario_id, groupEditTargetId, groupReqBody)
            .then((res: any) => {
              const successMsg = res?.data?.message || res?.message || 'Satellite group updated successfully';
              showToastMessage('success', successMsg);
              // Once API resolves, refresh in background
              const newChildIds = children.map(c => c.id);
              refreshSatellitesFromBackend(newChildIds).catch(err => {
                console.error('[SatelliteGroup] Background refresh failed', err);
              });
            })
            .catch(err => {
              console.error('[SatelliteGroup] Edit API failed', err);
              const errorMsg = err?.response?.data?.message || err?.response?.data?.Message || err?.data?.message || err?.message || 'Failed to update satellite group on backend';
              showToastMessage('error', errorMsg);
            });
          return;
        }

        // Create mode flow
        const res: any = await createSatelliteGroup(mission_id, scenario_id, groupReqBody);
        const created = unwrapCreateResponse(res);
        const returnedSats = created?.satellites ?? created?.children ?? [];
        const childrenWithBackend = children.map((child, idx) => {
          const satPayload = Array.isArray(returnedSats) ? returnedSats[idx] : null;
          const ephemeris = satPayload ? extractEphemeris(satPayload) : undefined;
          return {
            ...child,
            ephemeris,
            propagationStatus: ephemeris ? ('ready' as const) : ('preview' as const),
            effectivePerturbationForces: satPayload?.effectivePerturbationForces ?? satPayload?.perturbationForces ?? child.effectivePerturbationForces,
          };
        });
        addSatellite({
          type: 'group',
          id: created?.id || `group-${Date.now()}`,
          name: groupName || 'Group_01',
          dot: 'ok' as const,
          expanded: true,
          active: true,
          children: childrenWithBackend,
        });
        invalidateAccessWindows();

        const successMsg = res?.data?.message || res?.message || 'Satellite group created';
        showToastMessage('success', successMsg);

        cesium.clearOrbitPreview();
        closeAddSat();
        selectTreeItem(null, null);

        refreshSatellitesFromBackend().catch(err => {
          console.error('[SatelliteGroup] Background refresh failed', err);
        });

        return;
      } catch (err: any) {
        console.error(groupEditMode ? '[SatelliteGroup] Update failed' : '[SatelliteGroup] Create failed', err);
        const errorMessage = err?.response?.data?.message || err?.response?.data?.Message || err?.data?.message || err?.message || (groupEditMode ? 'Failed to update satellite group' : 'Failed to create satellite group');
        setFormErrors({ orbit: errorMessage });
        showToastMessage('error', errorMessage);
        return;
      } finally {
        setCreating(false);
      }

    } else {
      const norm = normalizeTlePair(tleLine1, tleLine2);
      const validationErrors: ValidationErrors = tleEntries.length > 0
        ? {}
        : {
          ...validateName(tleName, 'Satellite name', satellites, satEditTarget?.id),
          ...(norm ? {} : { tle: 'Paste or upload a valid TLE pair' }),
        };
      const tlePairError = norm ? validateTlePair(norm.line1, norm.line2) : '';
      if (tlePairError) validationErrors.tle = tlePairError;
      if (Object.keys(validationErrors).length > 0) {
        setFormErrors(validationErrors);
        if (validationErrors.tle) setTleError(validationErrors.tle);
        const errMsgs = Object.values(validationErrors).filter(Boolean);
        if (errMsgs.length > 0) {
          showToastMessage('error', errMsgs.join('. '));
        }
        return;
      }
      const entries: TleEntry[] = tleEntries.length > 0
        ? tleEntries
        : [{ name: tleName || 'TLE_Custom', line1: tleLine1, line2: tleLine2 }];

      if ((satEditMode && satEditTarget) || (groupEditMode && groupEditTarget)) {
        const targetId = satEditTarget?.id ?? groupEditTarget?.children[0]?.id;
        const targetGroupId = satEditTarget?.simulationGroupId ?? groupEditTarget?.id;
        if (!targetId || !targetGroupId) return;

        const entry = entries[0];
        const name = entry.name || tleName || 'TLE_Custom';
        const tleMeta = { source: 'custom_tle', line1: entry.line1, line2: entry.line2 };
        const isTleNameOnly = satEditTarget?.tleMeta?.line1 === entry.line1 &&
          satEditTarget?.tleMeta?.line2 === entry.line2;

        invalidateAccessWindows();

        if (groupEditMode && groupEditTarget) {
          const updatedSatellites = satellites.map(item => {
            if ((item as SatGroup).type === 'group' && item.id === groupEditTarget.id) {
              const grp = item as SatGroup;
              return {
                ...grp,
                name,
                tleMeta,
                children: grp.children.map((c, idx) => idx === 0 ? { ...c, name, tleMeta } : c),
              };
            }
            return item;
          });
          setSatellites(updatedSatellites);
        } else {
          updateSatellite(targetId, {
            name,
            tleMeta,
          });
        }

        const editBody = {
          name,
          satellites: [
            {
              id: targetId,
              name,
              tleLines: {
                line1: entry.line1,
                line2: entry.line2,
              }
            }
          ]
        };

        setCreating(true);
        editSatelliteGroup(mission_id, scenario_id, targetGroupId, editBody)
          .then((res: any) => {
            const successMsg = res?.data?.message || res?.message || 'Satellite updated successfully';
            showToastMessage('success', successMsg);
            refreshSatellitesFromBackend([targetId]).catch(err => {
              console.error('[Satellite] Background refresh failed', err);
            });
            cesium.clearOrbitPreview();
            closeAddSat();
          })
          .catch(err => {
            console.error('[Satellite] Edit API failed', err);
            const errorMsg = err?.response?.data?.message || err?.response?.data?.Message || err?.data?.message || err?.message || 'Failed to update satellite on backend';
            showToastMessage('error', errorMsg);
          })
          .finally(() => {
            setCreating(false);
          });
      } else {
        (async () => {
          try {
            setCreating(true);
            const epoch = Date.now() / 1000;
            const formData = new FormData();

            let tleText = tleTextPaste;
            if (!tleText.trim()) {
              tleText = entries.map(e => `${e.name || tleName || 'TLE_Custom'}\n${e.line1}\n${e.line2}`).join('\n');
            }

            const fileBlob = new Blob([tleText], { type: 'text/plain' });
            formData.append('file', fileBlob, 'tle.txt');
            formData.append('name', tleName || 'TLE_Custom');
            formData.append('epoch', epoch.toString());

            const res: any = await uploadTle(mission_id, scenario_id, formData);
            const createdRaw = unwrapCreateResponse(res);
            const created = Array.isArray(createdRaw) ? createdRaw[0] : createdRaw;

            const returnedSats = created?.satellites ?? created?.children ?? [];
            const childrenWithBackend = entries.map((entry, idx) => {
              const satPayload = Array.isArray(returnedSats) ? returnedSats[idx] : null;
              const ephemeris = satPayload ? extractEphemeris(satPayload) : undefined;
              return {
                id: satPayload?.id || `sat-tle-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 5)}`,
                name: entry.name || tleName || `TLE_${idx + 1}`,
                dot: 'ok' as const,
                active: true,
                tleMeta: { source: 'custom_tle', line1: entry.line1, line2: entry.line2 },
                physicalCharacteristics: DEFAULT_INTERNAL_PHYSICAL_CHARACTERISTICS,
                ephemeris,
                propagationStatus: ephemeris ? ('ready' as const) : ('preview' as const),
                effectivePerturbationForces: satPayload?.effectivePerturbationForces ?? satPayload?.perturbationForces ?? modelData?.perturbationForces ?? undefined,
              };
            });

            addSatellite({
              type: 'group',
              id: created?.id || `group-${Date.now()}`,
              name: tleName || 'TLE_Custom',
              dot: 'ok' as const,
              expanded: true,
              active: true,
              children: childrenWithBackend,
            });

            const successMsg = res?.data?.message || res?.message || 'Satellite created successfully';
            showToastMessage('success', successMsg);

            cesium.clearOrbitPreview();
            closeAddSat();
            selectTreeItem(null, null);

            refreshSatellitesFromBackend().catch(err => {
              console.error('[Satellite] Background refresh failed', err);
            });
          } catch (err: any) {
            console.error('[Satellite] TLE Upload API failed', err);
            const errorMsg = err?.response?.data?.message || err?.response?.data?.Message || err?.data?.message || err?.message || 'Failed to upload TLE to backend';
            showToastMessage('error', errorMsg);
          } finally {
            setCreating(false);
          }
        })();
        return;
      }
    }
    cesium.clearOrbitPreview();
    closeAddSat();
    selectTreeItem(null, null);
  };

  const tleNorm = normalizeTlePair(tleLine1, tleLine2);
  const tleComplete = !!tleNorm && !validateTlePair(tleNorm.line1, tleNorm.line2);

  const TABS: { id: TabId; label: string; icon: React.ReactNode }[] = [
    { id: 'satellite', label: 'Satellite', icon: <SatIcon /> },
    { id: 'group', label: 'Satellite Group', icon: <GroupIcon /> },
    { id: 'tle', label: 'TLE', icon: <TleIcon /> },
  ];

  return (
    <div
      className={styles.panel}
      style={{ ...dragStyle }}
      onPointerDown={e => e.stopPropagation()}
      onPointerUp={e => e.stopPropagation()}
      onMouseDown={e => e.stopPropagation()}
      onMouseUp={e => e.stopPropagation()}
      onClick={e => e.stopPropagation()}
      onContextMenu={e => e.stopPropagation()}
    >
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.headerIcon}><SatIcon /></span>
          <span className={styles.headerTitle}>SATELLITE</span>
        </div>
        <div className={styles.headerControls}>
          <CustomToolTip title={minimized ? 'Restore' : 'Minimize'} placement="bottom" className={styles.tooltipWrap}>
            <button className={styles.closeBtn} type="button" onClick={() => setMinimized(m => !m)} onPointerDown={e => e.stopPropagation()}>
              <MinimizeIcon minimized={minimized} />
            </button>
          </CustomToolTip>
          <CustomToolTip title="Close" placement="bottom" className={styles.tooltipWrap}>
            <button className={styles.closeBtn} type="button" onClick={closeAddSat} onPointerDown={e => e.stopPropagation()}>
              <CloseIcon />
            </button>
          </CustomToolTip>
        </div>
      </div>

      {!minimized && (
        <>
          {/* Tabs */}
          <div className={styles.tabs}>
            {TABS.map(t => (
              <button
                key={t.id}
                className={`${styles.tab} ${activeTab === t.id ? styles.tabActive : ''} ${(groupEditMode || satEditMode) && activeTab !== t.id ? styles.tabDisabled : ''}`}
                type="button"
                disabled={(groupEditMode || satEditMode) && activeTab !== t.id}
                onClick={() => {
                  if (groupEditMode || satEditMode) return;
                  setActiveTab(t.id);
                }}
              >
                <span className={styles.tabInner}>
                  <span className={styles.tabIconBox}>{t.icon}</span>
                  <span className={styles.tabLabel}>{t.label}</span>
                </span>
              </button>
            ))}
          </div>

          {/* Body */}
          <div className={styles.body}>
            {/* ── SATELLITE TAB ── */}
            {activeTab === 'satellite' && (
              <div className={styles.form}>
                <div className={styles.fieldStack}>
                  <FieldLabel title="Satellite Name">Name</FieldLabel>
                  <div className={`${styles.fieldInput} ${formErrors.name ? styles.fieldInputError : ''}`}>
                    <input
                      value={satName}
                      maxLength={NAME_MAX_LENGTH}
                      onChange={e => {
                        const inputValue = e.target.value;
                        const enforced = enforceNameConstraints(inputValue);
                        setSatName(enforced);
                        clearFormError('name');

                        // Check if character limit reached
                        if (enforced.length === NAME_MAX_LENGTH) {
                          showToastMessage('info', `Character limit of ${NAME_MAX_LENGTH} reached`);
                        }

                        // Check if special characters were rejected
                        if (inputValue !== enforced && inputValue.length > enforced.length) {
                          const specialCharError = checkSpecialCharacters(inputValue);
                          if (specialCharError) {
                            showToastMessage('info', specialCharError);
                          }
                        }
                      }}
                      placeholder="Satellite_01"
                    />
                  </div>
                </div>

                <div className={styles.fieldStackCol}>
                  <div className={styles.fieldLabelBig}>ORBIT</div>
                  <SelectInput
                    value={orbit.orbitType}
                    options={ORBIT_OPTS}
                    onChange={v => {
                      clearFormError('inclination');
                      clearFormError('eccentricity');
                      clearFormError('orbit');
                      patchOrbit(orbitTypePatch(v as OrbitType, orbit));
                    }}
                    style={{ width: '100%' }}
                    dropdownWidth={296}
                  />
                </div>

                <OrbitalDetailsForm s={orbit} set={patchOrbit} allowInputType={orbit.orbitType === 'inclined'} errors={formErrors} clearError={clearFormError} />
              </div>
            )}

            {/* ── GROUP TAB ── */}
            {activeTab === 'group' && (
              <>
                <div className={styles.form}>
                  <div className={styles.fieldStack}>
                    <FieldLabel title="Satellite Group Name">Group Name</FieldLabel>
                    <div className={`${styles.fieldInput} ${formErrors.name ? styles.fieldInputError : ''}`}>
                      <input
                        value={groupName}
                        maxLength={NAME_MAX_LENGTH}
                        onChange={e => {
                          const inputValue = e.target.value;
                          const enforced = enforceNameConstraints(inputValue);
                          setGroupName(enforced);
                          clearFormError('name');

                          // Check if character limit reached
                          if (enforced.length === NAME_MAX_LENGTH) {
                            showToastMessage('info', `Character limit of ${NAME_MAX_LENGTH} reached`);
                          }

                          // Check if special characters were rejected
                          if (inputValue !== enforced && inputValue.length > enforced.length) {
                            const specialCharError = checkSpecialCharacters(inputValue);
                            if (specialCharError) {
                              showToastMessage('info', specialCharError);
                            }
                          }
                        }}
                        placeholder="Group_01"
                      />
                    </div>
                  </div>
                </div>

                <AccordionSection
                  title="Reference Orbit"
                  isOpen={refOrbitOpen}
                  onToggle={(open) => setRefOrbitOpen(open)}
                >
                  <div className={styles.fieldStackCol}>
                    <div className={styles.fieldLabelBig}>ORBIT</div>
                    <SelectInput
                      value={refOrbit.orbitType}
                      options={GROUP_ORBIT_OPTS}
                      onChange={v => {
                        clearFormError('inclination');
                        clearFormError('eccentricity');
                        clearFormError('orbit');
                        patchRefOrbit(orbitTypePatch(v as OrbitType, refOrbit));
                      }}
                      style={{ width: '100%' }}
                      dropdownWidth={296}
                    />
                  </div>
                  <OrbitalDetailsForm s={refOrbit} set={patchRefOrbit} allowInputType={false} errors={formErrors} clearError={clearFormError} />
                </AccordionSection>

                <AccordionSection
                  title="Group Configurations"
                  isOpen={groupConfigOpen}
                  onToggle={(open) => setGroupConfigOpen(open)}
                >
                  <div className={styles.fieldStack}>
                    <TooltipText title="Group Type" className={styles.fsSubLabel}>Group Type</TooltipText>
                    {groupEditMode ? (
                      <div className={`${styles.selectInput} ${styles.fieldDisabled}`}>
                        <span>Single Plane</span>
                        <ChevronDown />
                      </div>
                    ) : (
                      <SelectInput
                        value={groupType}
                        options={GROUP_TYPE_OPTS}
                        onChange={v => setGroupType(v as GroupType)}
                      />
                    )}
                  </div>

                  <div className={styles.fieldRow2cfg}>
                    <TooltipText
                      title={groupType === 'multi' ? 'Satellites per Plane' : 'Number of Satellites'}
                      className={styles.cfgLabel}
                    >
                      {groupType === 'multi' ? 'Satellites per Plane' : 'Number of Satellites'}
                    </TooltipText>
                    <NumericInput
                      value={numSats}
                      onChange={v => {
                        const n = parseInt(v, 10);
                        const minVal = ORBIT_LIMITS.groupSatellites.min;
                        const maxVal = ORBIT_LIMITS.groupSatellites.max;
                        if (!isNaN(n) && (n < 0 || n > maxVal || (v.length >= 2 && n < minVal))) {
                          showToastMessage('info', `Number of Satellites must be between ${minVal} and ${maxVal}`);
                          return;
                        }
                        clearFormError('numSats');
                        setNumSats(v);
                      }}
                      error={!!formErrors.numSats}
                      width={90}
                    />
                  </div>

                  {groupType === 'multi' && (
                    <>
                      <div className={styles.fieldRow2cfg}>
                        <TooltipText title="Number of Planes" className={styles.cfgLabel}>Number of Planes</TooltipText>
                        <NumericInput
                          value={numPlanes}
                          onChange={v => {
                            const n = parseInt(v, 10);
                            const minVal = ORBIT_LIMITS.groupPlanes.min;
                            const maxVal = ORBIT_LIMITS.groupPlanes.max;
                            if (!isNaN(n) && (n < 0 || n > maxVal || (v.length >= 2 && n < minVal))) {
                              showToastMessage('info', `Number of Planes must be between ${minVal} and ${maxVal}`);
                              return;
                            }
                            clearFormError('numPlanes');
                            setNumPlanes(v);
                          }}
                          error={!!formErrors.numPlanes}
                          width={90}
                        />
                      </div>

                      <div className={styles.fieldRow2cfg}>
                        <TooltipText title="Plane Separation Angle" className={styles.cfgLabel}>Plane Separation Angle</TooltipText>
                        <div className={styles.fieldWithIcon}>
                          <CustomToolTip title="Even spacing" placement="top" className={styles.tooltipWrap}>
                            <button
                              className={`${styles.evenBtn} ${evenSpacing ? styles.evenBtnActive : ''}`}
                              type="button"
                              onClick={() => { clearFormError('planeSepAngle'); setEvenSpacing(v => !v); }}
                            >
                              <EvenIcon />
                            </button>
                          </CustomToolTip>
                          <NumericInput
                            value={planeSepAngle}
                            onChange={v => {
                              const sanitizedVal = limitDecimalPlaces(v);
                              const angle = groupAngleUnit === 'rad' ? (parseFloat(sanitizedVal) || 0) * (180 / Math.PI) : parseFloat(sanitizedVal) || 0;
                              const minVal = ORBIT_LIMITS.groupAngleDeg.min;
                              const maxVal = refOrbit.orbitType === 'sso' ? 180 : ORBIT_LIMITS.groupAngleDeg.max;
                              if (!isNaN(angle) && (angle < minVal || angle > maxVal)) {
                                showToastMessage('info', `Plane Separation Angle must be between ${minVal}° and ${maxVal}°`);
                                return;
                              }
                              clearFormError('planeSepAngle');
                              setPlaneSepAngle(sanitizedVal);
                            }}
                            error={!!formErrors.planeSepAngle}
                            disabled={evenSpacing}
                            suffix={groupAngleLabel}
                            onSuffixClick={!evenSpacing ? handleGroupAngleToggle : undefined}
                            suffixTitle="Click to toggle °/rad"
                            width={100}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  <div className={styles.fieldRow2cfg}>
                    <TooltipText title="Inter Plane Phasing" className={styles.cfgLabel}>Inter Plane Phasing</TooltipText>
                    <div className={styles.fieldWithIcon}>
                      <CustomToolTip title="Auto phasing" placement="top" className={styles.tooltipWrap}>
                        <button
                          className={`${styles.evenBtn} ${evenInterPhasing ? styles.evenBtnActive : ''}`}
                          type="button"
                          onClick={() => { clearFormError('interPlanePhasing'); setEvenInterPhasing(v => !v); }}
                        >
                          <PhasingIcon />
                        </button>
                      </CustomToolTip>
                      <NumericInput
                        value={interPlanePhasing}
                        onChange={v => {
                          const sanitizedVal = limitDecimalPlaces(v);
                          const angle = groupAngleUnit === 'rad' ? (parseFloat(sanitizedVal) || 0) * (180 / Math.PI) : parseFloat(sanitizedVal) || 0;
                          const minVal = ORBIT_LIMITS.groupAngleDeg.min;
                          const maxVal = refOrbit.orbitType === 'sso' ? 180 : ORBIT_LIMITS.groupAngleDeg.max;
                          if (!isNaN(angle) && (angle < minVal || angle > maxVal)) {
                            showToastMessage('info', `Inter Plane Phasing must be between ${minVal}° and ${maxVal}°`);
                            return;
                          }
                          clearFormError('interPlanePhasing');
                          setInterPlanePhasing(sanitizedVal);
                        }}
                        error={!!formErrors.interPlanePhasing}
                        disabled={evenInterPhasing}
                        suffix={groupAngleLabel}
                        onSuffixClick={!evenInterPhasing ? handleGroupAngleToggle : undefined}
                        suffixTitle="Click to toggle °/rad"
                        width={100}
                      />
                    </div>
                  </div>
                </AccordionSection>
              </>
            )}

            {/* ── TLE TAB ── */}
            {activeTab === 'tle' && (
              <div className={styles.form}>
                {/* Name — required */}
                <div className={styles.fieldStack}>
                  <FieldLabel title="Satellite Name">Name</FieldLabel>
                  <div className={`${styles.fieldInput} ${formErrors.name ? styles.fieldInputError : ''}`}>
                    <input
                      value={tleName}
                      maxLength={NAME_MAX_LENGTH}
                      onChange={e => {
                        const inputValue = e.target.value;
                        const enforced = enforceNameConstraints(inputValue);
                        setTleName(enforced);
                        clearFormError('name');

                        // Check if character limit reached
                        if (enforced.length === NAME_MAX_LENGTH) {
                          showToastMessage('info', `Character limit of ${NAME_MAX_LENGTH} reached`);
                        }

                        // Check if special characters were rejected
                        if (inputValue !== enforced && inputValue.length > enforced.length) {
                          const specialCharError = checkSpecialCharacters(inputValue);
                          if (specialCharError) {
                            showToastMessage('info', specialCharError);
                          }
                        }
                      }}
                      placeholder="Satellite_01"
                    />
                  </div>
                </div>

                <TleInput
                  value={tleTextPaste}
                  onChange={handleTleTextChange}
                  onPaste={handleTleTextPaste}
                  onBlur={handleTleTextBlur}
                  hasError={!!formErrors.tle}
                  tleComplete={tleComplete}
                  tleError={tleError}
                  onFileUpload={handleTleFileUpload}
                  tleEntriesCount={tleEntries.length}
                  tlePreviewLoading={tlePreviewLoading}
                  isUploaded={isTleFileUploaded}
                  uploadedFileName={uploadedFileName}
                  onClearFile={handleClearTleFile}
                />
              </div>
            )}
          </div>

          {/* Footer */}
          <div className={styles.footer}>
            {activeTab === 'group' && groupStep === 1 ? (
              <>
                <button className={styles.cancelBtn} type="button" onClick={closeAddSat} disabled={creating}>Close</button>
                <button className={styles.createBtn} type="button" onClick={handleNextGroupStep} disabled={creating}>Next</button>
              </>
            ) : activeTab === 'group' && groupStep === 2 ? (
              <>
                <button className={styles.cancelBtn} type="button" onClick={() => setGroupStep(1)} disabled={creating}>Back</button>
                <button className={styles.createBtn} type="button" onClick={handleCreate} disabled={creating}>
                  {creating ? (groupEditMode ? 'Updating...' : 'Creating...') : (groupEditMode ? 'Update' : 'Create')}
                </button>
              </>
            ) : (
              <>
                <button className={styles.cancelBtn} type="button" onClick={closeAddSat} disabled={creating}>Close</button>
                <button className={styles.createBtn} type="button" onClick={handleCreate} disabled={creating}>
                  {creating ? (groupEditMode || satEditMode ? 'Updating...' : 'Creating...') : (groupEditMode || satEditMode ? 'Update' : 'Create')}
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AddSatModal;
